import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import { UserContext } from '../App';
import { FaSpinner } from 'react-icons/fa';
import Select from 'react-select';
import * as XLSX from 'xlsx';
import Logo from './Logo';

// --- Modal para Crear/Actualizar Catálogo ---
function CatalogModal({ isOpen, mode, initialName, onClose, onSubmit }) {
  const [catalogName, setCatalogName] = useState(initialName || '');
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  

  useEffect(() => {
    if (!isOpen) {
      setCatalogName(initialName || '');
      setFile(null);
      setError('');
      setLoading(false);
    }
  }, [isOpen, initialName]);

  const validateName = (name) => /^[a-zA-Z0-9_\-\/\(\)]+$/.test(name);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (mode === 'create' && !catalogName.trim()) {
      setError('Debes indicar el nombre del catálogo.');
      return;
    }
    if (mode === 'update' && !initialName) {
      setError('No se pudo obtener el nombre del catálogo a actualizar.');
      return;
    }
    if (!file) {
      setError('Selecciona un archivo Excel.');
      return;
    }
    if (mode === 'create' && !validateName(catalogName)) {
      setError('Nombre de catálogo inválido. Sin espacios, solo letras, números, guiones, paréntesis.');
      return;
    }
    setLoading(true);
    try {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = async () => {
        const b64 = reader.result.toString().replace(/^data:(.*,)?/, '');
        const data = {
          excel_query: b64,
          catalog_id: mode === 'update' ? initialName : catalogName.trim(),
          tenant_id: localStorage.getItem('tenant_id')
        };
        await onSubmit(data);
        onClose();
      };
      reader.onerror = () => {
        setError('Error al leer el archivo.');
        setLoading(false);
      };
    } catch (e) {
      setError('Error al procesar el catálogo.');
      setLoading(false);
    }
  };

  if (!isOpen) return null;
  return (
    <div className="modal-overlay">
      <div className="modal-content animate-fade-in">
        <h3>{mode === 'create' ? 'Crear nuevo catálogo' : `Actualizar catálogo "${initialName}"`}</h3>
        <form onSubmit={handleSubmit}>
          {mode === 'create' && (
            <div className="form-group">
              <label className="form-label">Nombre del catálogo</label>
              <input
                type="text"
                className="form-input"
                value={catalogName}
                onChange={e => setCatalogName(e.target.value.replace(/\s/g, ''))}
                placeholder="Ej: MYCATALOG_2025"
                maxLength={50}
              />
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Archivo Excel</label>
            <input
              type="file"
              accept=".xlsx,.xls"
              className="form-input"
              onChange={e => setFile(e.target.files[0])}
            />
          </div>
          {error && <p className="error-message">{error}</p>}
          <div className="form-actions" style={{ justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-outline" onClick={onClose} disabled={loading}>
              Cancelar
            </button>
            <button type="submit" className={`btn btn-primary ${loading ? 'btn-loading' : ''}`} disabled={loading}>
              {loading ? (
                <FaSpinner className="spin" style={{ marginRight: '8px' }} />
              ) : null}
              <span>
                {loading ? 'Procesando...' : (mode === 'create' ? 'Crear catálogo' : 'Actualizar catálogo')}
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// --- Modal para Seleccionar Categoría ---
function CategorySelectionModal({ isOpen, categories, selectedCategory, onCategoryChange, onClose, onContinue, onCreateCategory, newCategoryName, onNewCategoryNameChange, creatingCategory, loadingCategories }) {
  const [showCreateSection, setShowCreateSection] = useState(false);
  
  if (!isOpen) return null;
  
  return (
    <div className="modal-overlay">
      <div className="modal-content animate-fade-in">
        <h3>Seleccionar Categoría</h3>
        <p className="modal-subtitle">Primero selecciona la categoría a la que pertenecerá el nuevo catálogo</p>
        
        {loadingCategories ? (
          <div className="loading-container">
            <div className="loading-spinner-small"></div>
            <p>Cargando categorías...</p>
          </div>
        ) : (
          <div className="category-selection-content">
            <div className="form-group">
              <label className="form-label">Categorías disponibles</label>
              <select
                className="form-select"
                value={selectedCategory}
                onChange={e => onCategoryChange(e.target.value)}
              >
                <option value="">Selecciona una categoría...</option>
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
            
            <div className="create-category-toggle">
              <button 
                type="button"
                className="btn btn-outline btn-sm"
                onClick={() => setShowCreateSection(!showCreateSection)}
              >
                {showCreateSection ? '❌ Cancelar' : '➕ Crear nueva categoría'}
              </button>
            </div>
            
            {showCreateSection && (
              <div className="new-category-section">
                <h4>Crear nueva categoría</h4>
                <div className="form-group">
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Nombre de la nueva categoría..."
                      value={newCategoryName}
                      onChange={e => onNewCategoryNameChange(e.target.value)}
                    />
                    <button 
                      type="button"
                      className="btn btn-secondary"
                      onClick={onCreateCategory}
                      disabled={!newCategoryName.trim() || creatingCategory}
                    >
                      {creatingCategory ? 'Creando...' : 'Crear'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        
        <div className="form-actions" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-outline" onClick={onClose}>
            Cancelar
          </button>
          <button 
            type="button" 
            className="btn btn-primary" 
            onClick={onContinue}
            disabled={!selectedCategory || loadingCategories}
          >
            Continuar
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Componente de Resumen de Alertas ---
function AlertSummary({ totalAlerts, validationErrors, priceWarnings, onShowDetails }) {
  if (totalAlerts === 0) return null;
  
  return (
    <div className="alert-summary">
      <div className="alert-summary-content">
        <div className="alert-summary-icon">
          <span className="alert-icon">⚠️</span>
          <span className="alert-count">{totalAlerts}</span>
        </div>
        <div className="alert-summary-text">
          <span className="alert-title">Alertas detectadas</span>
          <div className="alert-breakdown">
            {validationErrors.length > 0 && (
              <span className="error-count">
                {validationErrors.length} errores críticos
              </span>
            )}
            {priceWarnings.length > 0 && (
              <span className="warning-count">
                {priceWarnings.length} cambios significativos de precio
              </span>
            )}
          </div>
        </div>
        <button 
          className="alert-details-btn"
          onClick={onShowDetails}
          title="Ver detalles de las alertas"
        >
          Ver detalles
        </button>
      </div>
    </div>
  );
}

// --- Modal para Editar Marca y Modelo ---
function EditManufacturerModelModal({ isOpen, onClose, onSave, initialData }) {
  const [manufacturer, setManufacturer] = useState(initialData?.manufacturer || '');
  const [model, setModel] = useState(initialData?.model || '');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && initialData) {
      setManufacturer(initialData.manufacturer || '');
      setModel(initialData.model || '');
    }
  }, [isOpen, initialData]);

  const handleSave = async () => {
    if (!manufacturer.trim() || !model.trim()) {
      return;
    }

    setLoading(true);
    try {
      await onSave({
        manufacturer: manufacturer.trim(),
        model: model.trim()
      });
      onClose();
    } catch (error) {
      console.error('Error saving manufacturer/model:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content animate-fade-in" style={{ maxWidth: '500px' }}>
        <div className="modal-header">
          <h3>Editar Marca y Modelo</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Marca</label>
            <input
              type="text"
              className="form-input"
              value={manufacturer}
              onChange={(e) => setManufacturer(e.target.value)}
              placeholder="Ej: SAMSUNG"
              autoFocus
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Modelo</label>
            <input
              type="text"
              className="form-input"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              placeholder="Ej: GALAXY S21"
            />
          </div>
        </div>
        
        <div className="modal-footer">
          <button 
            type="button" 
            className="btn btn-outline" 
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </button>
          <button 
            type="button" 
            className="btn btn-primary" 
            onClick={handleSave}
            disabled={!manufacturer.trim() || !model.trim() || loading}
          >
            {loading ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Modal para Procesamiento de Excel ---
function ExcelProcessingModal({ 
  isOpen, 
  onClose, 
  step, 
  sheets, 
  selectedSheet, 
  onSheetSelect, 
  headers, 
  data, 
  columnMapping, 
  onColumnMappingUpdate, 
  requiredFields, 
  isMappingComplete, 
  onProceedToValidation, 
  validationData, 
  groupedValidationData,
  expandedGroups,
  expandedModels,
  onToggleGroup,
  onToggleModel,
  onExpandMultipleGroups,
  onExpandMultipleModels,
  onUpdateValidationData,
  getRecordCounts,
  getManufacturerCounts,
  getTotalCounts,
  validationErrors,
  hasValidationErrors,
  hasFieldError,
  getFieldError,
  hasManufacturerErrors,
  hasModelErrors,
  hasManufacturerWarnings,
  hasModelWarnings,
  hasFieldWarning,
  getFieldWarning,
  hasPriceChanges,
  priceWarnings,
  hasPriceWarnings,
  totalAlerts,
  onFinalize,
  onFileUpload,
  onBackToMapping,
  message,
  isError,
  isUpdatingCatalog,
  catalogToUpdate,
  catalogUpdateCategory,
  handleNumericInputChange,
  openEditManufacturerModal,
  hasZeroValue,
  formatCurrency,
  hasManufacturerRecordsToUpload,
  hasModelRecordsToUpload,
  hasZeroChangeExisting
}) {
  const [catalogName, setCatalogName] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setCatalogName('');
      setUploadFile(null);
      setDragActive(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      setUploadFile(file);
      handleFileUpload(file);
    }
  };

  const handleFileUpload = (file) => {
    if (step === 'upload' && onFileUpload) {
      onFileUpload(file);
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setUploadFile(file);
      handleFileUpload(file);
    }
  };

  const renderStepIndicator = () => {
    const steps = [
      { key: 'upload', label: 'Cargar Archivo', icon: '📁' },
      { key: 'sheet-selection', label: 'Seleccionar Hoja', icon: '📄' },
      { key: 'column-mapping', label: 'Mapear Columnas', icon: '🔗' },
      { key: 'data-validation', label: 'Validar Datos', icon: '✅' }
    ];

    const currentStepIndex = steps.findIndex(s => s.key === step);

    return (
      <div className="step-indicator">
        {steps.map((stepItem, index) => (
          <div key={stepItem.key} className={`step-item ${index <= currentStepIndex ? 'active' : ''} ${index === currentStepIndex ? 'current' : ''}`}>
            <div className="step-icon">{stepItem.icon}</div>
            <div className="step-label">{stepItem.label}</div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content excel-modal">
        <div className="modal-header">
          <h3 className="modal-title">
            {isUpdatingCatalog 
              ? `Actualizando Catálogo (${catalogUpdateCategory}): ${catalogToUpdate.includes('#') ? catalogToUpdate.split('#')[1] : catalogToUpdate}`
              : 'Crear Nuevo Catálogo de Precios'
            }
          </h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {renderStepIndicator()}

        {message && (
          <div className={`message ${isError ? 'message-error' : 'message-success'}`}>
            {message}
          </div>
        )}

        <div className="modal-body">
          {/* Paso 1: Cargar Archivo */}
          {step === 'upload' && (
            <div className="upload-section">
              <h4>Selecciona tu archivo Excel</h4>
              <div 
                className={`upload-area ${dragActive ? 'drag-active' : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <div className="upload-content">
                  <div className="upload-icon">📊</div>
                  <p className="upload-text">
                    Arrastra tu archivo Excel aquí o <span className="upload-link">haz clic para seleccionar</span>
                  </p>
                  <p className="upload-hint">Formatos soportados: .xlsx, .xls</p>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileInputChange}
                    className="upload-input"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Paso 2: Selección de Hoja */}
          {step === 'sheet-selection' && (
            <div className="sheet-selection">
              <h4>Selecciona la hoja a procesar</h4>
              <p>Tu archivo tiene múltiples hojas. Selecciona la que contiene los datos de precios:</p>
              <div className="sheet-list">
                {sheets.map(sheet => (
                  <button
                    key={sheet}
                    className={`sheet-item ${selectedSheet === sheet ? 'selected' : ''}`}
                    onClick={() => onSheetSelect(sheet)}
                  >
                    📄 {sheet}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Paso 3: Mapeo de Columnas */}
          {step === 'column-mapping' && (
            <div className="column-mapping">
              <h4>Mapear Columnas</h4>
              <p>Conecta las columnas de tu Excel con los campos requeridos:</p>
              
              <div className="mapping-grid">
                {requiredFields.map(field => (
                  <div key={field.key} className="mapping-row">
                    <div className="field-info">
                      <div className="field-header">
                        <span className="field-icon">{field.icon}</span>
                        <span className="field-label">{field.label}</span>
                        <span className="field-required">*</span>
                      </div>
                      <p className="field-description">{field.description}</p>
                    </div>
                    <div className="field-mapping">
                      <select
                        value={columnMapping[field.key] !== undefined ? columnMapping[field.key] : ''}
                        onChange={(e) => onColumnMappingUpdate(field.key, e.target.value)}
                        className="mapping-select"
                        data-field={field.key}
                      >
                        <option value="">Seleccionar columna...</option>
                        {headers.map((header, index) => (
                          <option key={index} value={index}>
                            {header}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mapping-actions">
                <button 
                  className="btn btn-primary"
                  onClick={onProceedToValidation}
                  disabled={!isMappingComplete()}
                >
                  {message && !isError ? '➡️ Continuar' : 'Continuar a Validación'}
                </button>
              </div>
            </div>
          )}

          {/* Paso 4: Validación de Datos */}
          {step === 'data-validation' && (
            <div className="data-validation">
              <h4>Validar y Finalizar</h4>
              <p>Revisa y edita los datos agrupados por marca y modelo:</p>
              
              {/* Resumen de alertas */}
              <AlertSummary 
                totalAlerts={totalAlerts}
                validationErrors={validationErrors}
                priceWarnings={priceWarnings}
                onShowDetails={() => {
                  // Expandir automáticamente grupos con alertas
                  const groupsWithAlerts = new Set();
                  const modelsWithAlerts = new Set();
                  
                  Object.entries(groupedValidationData).forEach(([manufacturer, models]) => {
                    if (hasManufacturerErrors(manufacturer) || hasManufacturerWarnings(manufacturer)) {
                      groupsWithAlerts.add(manufacturer);
                      
                      Object.entries(models).forEach(([model, records]) => {
                        if (hasModelErrors(manufacturer, model) || hasModelWarnings(manufacturer, model)) {
                          modelsWithAlerts.add(`${manufacturer}_${model}`);
                        }
                      });
                    }
                  });
                  
                  onExpandMultipleGroups(groupsWithAlerts);
                  onExpandMultipleModels(modelsWithAlerts);
                }}
              />
              
              <div className="validation-summary">
                <div className="summary-item">
                  <span className="summary-label">Total de registros:</span>
                  <span className="summary-value">{getTotalCounts(groupedValidationData).total}</span>
                  <span className="summary-detail">
                    {getTotalCounts(groupedValidationData).newCount} nuevos, {getTotalCounts(groupedValidationData).updateCount} actualizaciones
                  </span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Registros a enviar:</span>
                  <span className="summary-value optimization-count">
                    {validationData.filter(row => hasPriceChanges(row)).length}
                  </span>
                  <span className="summary-detail">
                    Solo registros con cambios (optimización)
                  </span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Marcas:</span>
                  <span className="summary-value">{Object.keys(groupedValidationData).length}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Errores:</span>
                  <span className={`summary-value ${hasValidationErrors ? 'error-count' : ''}`}>
                    {validationErrors.length}
                  </span>
                </div>
              </div>

              {hasValidationErrors && (
                <div className="validation-errors-alert">
                  <div className="alert alert-error">
                    <span className="alert-icon">⚠️</span>
                    <div className="alert-content">
                      <strong>¡Atención!</strong> Se encontraron {validationErrors.length} errores en los datos.
                      <br />
                      Los campos con errores están marcados en rojo. Corrige todos los errores antes de continuar.
                    </div>
                  </div>
                </div>
              )}

              <div className="grouped-data-preview">
                {Object.entries(groupedValidationData)
                  .sort(([manufacturerA], [manufacturerB]) => {
                    // Ordenar: primero los que se van a subir, después los que no
                    const aWillUpload = hasManufacturerRecordsToUpload(manufacturerA);
                    const bWillUpload = hasManufacturerRecordsToUpload(manufacturerB);
                    if (aWillUpload && !bWillUpload) return -1;
                    if (!aWillUpload && bWillUpload) return 1;
                    return manufacturerA.localeCompare(manufacturerB);
                  })
                  .map(([manufacturer, models]) => (
                  <div key={manufacturer} className="manufacturer-group">
                                          <div 
                        className={`group-header ${expandedGroups.has(manufacturer) ? 'expanded' : ''} ${hasManufacturerErrors(manufacturer) ? 'has-errors' : ''} ${hasManufacturerWarnings(manufacturer) ? 'has-warnings' : ''} ${!hasManufacturerRecordsToUpload(manufacturer) ? 'no-upload' : ''}`}
                        onClick={() => onToggleGroup(manufacturer)}
                      >
                      <span className={`expand-icon ${expandedGroups.has(manufacturer) ? 'rotated' : ''}`}>▶</span>
                      <span className="group-title">{manufacturer}</span>
                      <span className="group-count">
                                                        ({getManufacturerCounts(models).total} registros a subir: {getManufacturerCounts(models).newCount} nuevos, {getManufacturerCounts(models).updateCount} actualizaciones)
                      </span>
                      {hasManufacturerErrors(manufacturer) && (
                        <span className="error-indicator" title="Esta marca contiene errores críticos">🔴</span>
                      )}
                      {hasManufacturerWarnings(manufacturer) && (
                        <span className="warning-indicator" title="Esta marca contiene cambios significativos de precio">🟡</span>
                      )}
                    </div>
                    
                    {expandedGroups.has(manufacturer) && (
                      <div className="models-container">
                        {Object.entries(models)
                          .sort(([modelA], [modelB]) => {
                            // Ordenar: primero los modelos que se van a subir, después los que no
                            const aWillUpload = hasModelRecordsToUpload(manufacturer, modelA);
                            const bWillUpload = hasModelRecordsToUpload(manufacturer, modelB);
                            if (aWillUpload && !bWillUpload) return -1;
                            if (!aWillUpload && bWillUpload) return 1;
                            return modelA.localeCompare(modelB);
                          })
                          .map(([model, records]) => {
                          const modelKey = `${manufacturer}_${model}`;
                          const counts = getRecordCounts(records);
                          return (
                            <div key={model} className="model-group">
                              <div 
                                className={`model-header ${expandedModels.has(modelKey) ? 'expanded' : ''} ${hasModelErrors(manufacturer, model) ? 'has-errors' : ''} ${hasModelWarnings(manufacturer, model) ? 'has-warnings' : ''} ${!hasModelRecordsToUpload(manufacturer, model) ? 'no-upload' : ''}`}
                                onClick={() => onToggleModel(modelKey)}
                              >
                                <span className={`expand-icon ${expandedModels.has(modelKey) ? 'rotated' : ''}`}>▶</span>
                                <span className="model-title">📱 {model}</span>
                                <span className="model-count">
                                  ({counts.total} registros a subir: {counts.newCount} nuevos, {counts.updateCount} actualizaciones)
                                </span>
                                {hasModelErrors(manufacturer, model) && (
                                  <span className="error-indicator" title="Este modelo contiene errores críticos">🔴</span>
                                )}
                                {hasModelWarnings(manufacturer, model) && (
                                  <span className="warning-indicator" title="Este modelo contiene cambios significativos de precio">🟡</span>
                                )}
                              </div>
                              
                              {expandedModels.has(modelKey) && (
                                <div className="records-table">
                              <table>
                                <thead>
                                  <tr>
                                    <th>Marca / Modelo</th>
                                    <th>Almacenamiento</th>
                                    <th>Grado A</th>
                                    <th>Grado B</th>
                                    <th>Grado C</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {records.map((record, index) => (
                                    <tr key={record.rowIndex} className={`${record.isNew ? 'new-record' : 'existing-record'} ${hasPriceChanges(record) ? 'will-be-sent' : 'will-be-skipped'}`}>
                                      <td>
                                        <div className="manufacturer-model-cell">
                                          <div className="manufacturer-model-display">
                                            <strong>{record.data.manufacturer}</strong>
                                            <br />
                                            <span className="model-text">{record.data.model}</span>
                                          </div>
                                          <button
                                            type="button"
                                            className="edit-manufacturer-btn"
                                            onClick={() => openEditManufacturerModal(record.data.manufacturer, record.data.model)}
                                            title="Editar marca y modelo"
                                          >
                                            ✏️
                                          </button>
                                        </div>
                                      </td>
                                      <td>
                                        <div className="field-with-status">
                                          <input
                                            type="text"
                                            value={record.data.configuration || ''}
                                            onChange={(e) => onUpdateValidationData(record.rowIndex, 'configuration', e.target.value)}
                                            className={`inline-edit-input ${hasFieldError(record.rowIndex, 'configuration') ? 'error' : ''}`}
                                            title={getFieldError(record.rowIndex, 'configuration') || ''}
                                          />
                                          {!hasPriceChanges(record) && (
                                            <span className="skip-indicator" title="Este registro no será enviado (sin cambios)">⏭️</span>
                                          )}
                                        </div>
                                      </td>
                                      <td>
                                        <input
                                          type="text"
                                          value={record.data.grade_a ? Number(record.data.grade_a).toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}
                                          onChange={(e) => handleNumericInputChange(e, record.rowIndex, 'grade_a', onUpdateValidationData)}
                                          className={`inline-edit-input price-input ${hasFieldError(record.rowIndex, 'grade_a') ? 'error' : ''} ${hasFieldWarning(record.rowIndex, 'grade_a') ? 'warning' : ''} ${hasZeroValue(record.rowIndex, 'grade_a') ? 'zero-value' : ''} ${hasZeroChangeExisting(record.rowIndex, 'grade_a') ? 'zero-change-existing' : ''}`}
                                          placeholder="0,00"
                                                                                      title={getFieldError(record.rowIndex, 'grade_a') || getFieldWarning(record.rowIndex, 'grade_a') || (record.priceChanges && record.priceChanges.grade_a ? `Cambio: ${record.priceChanges.grade_a.percentChange}% (${formatCurrency(record.priceChanges.grade_a.oldPrice)} → ${formatCurrency(record.priceChanges.grade_a.newPrice)})` : (hasZeroValue(record.rowIndex, 'grade_a') ? 'Valor en cero' : ''))}
                                        />
                                      </td>
                                      <td>
                                        <input
                                          type="text"
                                          value={record.data.grade_b ? Number(record.data.grade_b).toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}
                                          onChange={(e) => handleNumericInputChange(e, record.rowIndex, 'grade_b', onUpdateValidationData)}
                                          className={`inline-edit-input price-input ${hasFieldError(record.rowIndex, 'grade_b') ? 'error' : ''} ${hasFieldWarning(record.rowIndex, 'grade_b') ? 'warning' : ''} ${hasZeroValue(record.rowIndex, 'grade_b') ? 'zero-value' : ''} ${hasZeroChangeExisting(record.rowIndex, 'grade_b') ? 'zero-change-existing' : ''}`}
                                          placeholder="0,00"
                                                                                      title={getFieldError(record.rowIndex, 'grade_b') || getFieldWarning(record.rowIndex, 'grade_b') || (record.priceChanges && record.priceChanges.grade_b ? `Cambio: ${record.priceChanges.grade_b.percentChange}% (${formatCurrency(record.priceChanges.grade_b.oldPrice)} → ${formatCurrency(record.priceChanges.grade_b.newPrice)})` : (hasZeroValue(record.rowIndex, 'grade_b') ? 'Valor en cero' : ''))}
                                        />
                                      </td>
                                      <td>
                                        <input
                                          type="text"
                                          value={record.data.grade_c ? Number(record.data.grade_c).toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}
                                          onChange={(e) => handleNumericInputChange(e, record.rowIndex, 'grade_c', onUpdateValidationData)}
                                          className={`inline-edit-input price-input ${hasFieldError(record.rowIndex, 'grade_c') ? 'error' : ''} ${hasFieldWarning(record.rowIndex, 'grade_c') ? 'warning' : ''} ${hasZeroValue(record.rowIndex, 'grade_c') ? 'zero-value' : ''} ${hasZeroChangeExisting(record.rowIndex, 'grade_c') ? 'zero-change-existing' : ''}`}
                                          placeholder="0,00"
                                                                                      title={getFieldError(record.rowIndex, 'grade_c') || getFieldWarning(record.rowIndex, 'grade_c') || (record.priceChanges && record.priceChanges.grade_c ? `Cambio: ${record.priceChanges.grade_c.percentChange}% (${formatCurrency(record.priceChanges.grade_c.oldPrice)} → ${formatCurrency(record.priceChanges.grade_c.newPrice)})` : (hasZeroValue(record.rowIndex, 'grade_c') ? 'Valor en cero' : ''))}
                                        />
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="catalog-name-input">
                <label className="form-label">
                  {isUpdatingCatalog ? 'Confirmar nombre del catálogo' : 'Nombre del catálogo'}
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={catalogName || (isUpdatingCatalog && catalogToUpdate.includes('#') ? catalogToUpdate.split('#')[1] : '')}
                  onChange={(e) => setCatalogName(e.target.value)}
                  placeholder={isUpdatingCatalog ? catalogToUpdate.includes('#') ? catalogToUpdate.split('#')[1] : catalogToUpdate : "Ej: FALABELLA_ENERO_2025"}
                  disabled={isUpdatingCatalog}
                />
                {isUpdatingCatalog && (
                  <small className="form-hint">
                    Al actualizar un catálogo, el nombre no se puede cambiar.
                  </small>
                )}
              </div>

              <div className="validation-actions">
                <button 
                  className="btn btn-outline"
                  onClick={onBackToMapping}
                >
                  Volver al Mapeo
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={() => onFinalize(catalogName || (isUpdatingCatalog && catalogToUpdate.includes('#') ? catalogToUpdate.split('#')[1] : catalogToUpdate))}
                  disabled={(!catalogName.trim() && !isUpdatingCatalog) || hasValidationErrors}
                  title={hasValidationErrors ? 'Corrige todos los errores antes de continuar' : ''}
                >
                  {isUpdatingCatalog ? 'Actualizar Catálogo' : 'Crear Catálogo'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Precios() {
  const { userData } = useContext(UserContext);
  const navigate = useNavigate();
  const tenantId = localStorage.getItem('tenant_id') || userData.tenant_id;

  // Campos requeridos para el mapeo de precios
  const requiredPriceFields = [
    {
      key: 'manufacturer',
      label: 'Marca',
      description: 'Nombre de la marca o empresa (ej: FALABELLA, ÉXITO)',
      icon: '🏢'
    },
    {
      key: 'model',
      label: 'Modelo',
      description: 'Modelo del dispositivo (ej: iPhone 14 Pro, Galaxy S23)',
      icon: '📱'
    },
    {
      key: 'configuration',
      label: 'Almacenamiento',
      description: 'Configuración de almacenamiento (ej: 128GB, 256GB)',
      icon: '💾'
    },
    {
      key: 'grade_a',
      label: 'Valor Grado A',
      description: 'Precio para dispositivos en grado A (excelente estado)',
      icon: '💰'
    },
    {
      key: 'grade_b',
      label: 'Valor Grado B',
      description: 'Precio para dispositivos en grado B (buen estado)',
      icon: '💵'
    },
    {
      key: 'grade_c',
      label: 'Valor Grado C',
      description: 'Precio para dispositivos en grado C (estado regular)',
      icon: '💴'
    }
  ];

  // Función para obtener headers con autenticación Cognito
  const getAuthHeaders = async () => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      
      if (!idToken) {
        throw new Error('No se pudo obtener el ID token');
      }
      
      return {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json'
      };
    } catch (error) {
      console.error('Error al obtener el token de autenticación:', error);
      throw error;
    }
  };

  // Estados para menú izquierdo
  const [catalogs, setCatalogs] = useState([]);
  const [catalogsByCategory, setCatalogsByCategory] = useState({});
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [selectedCatalog, setSelectedCatalog] = useState('');
  const [loadingCatalogs, setLoadingCatalogs] = useState(false);

  // Estados de filtros y precios (para columna derecha)
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [configs, setConfigs] = useState([]);

  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedConfig, setSelectedConfig] = useState('');
  
  const [prices, setPrices] = useState([]);
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [loadingFilters, setLoadingFilters] = useState(false);
  
  // Estados para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [hasMorePages, setHasMorePages] = useState(false);

  // Resúmenes y mensajes
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [updateSummary, setUpdateSummary] = useState(null);

  // Modales
  const [isCatalogModalOpen, setIsCatalogModalOpen] = useState(false);
  const [catalogModalMode, setCatalogModalMode] = useState('create');
  const [editingCatalogName, setEditingCatalogName] = useState('');
  
  // Estados para categorías
  const [availableCategories, setAvailableCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);

  // Estados para procesamiento avanzado de Excel
  const [excelProcessingStep, setExcelProcessingStep] = useState('upload'); // 'upload', 'sheet-selection', 'column-mapping', 'data-validation'
  const [excelWorkbook, setExcelWorkbook] = useState(null);
  const [excelSheets, setExcelSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState('');
  const [excelHeaders, setExcelHeaders] = useState([]);
  const [excelData, setExcelData] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [validationData, setValidationData] = useState([]);
  const [groupedValidationData, setGroupedValidationData] = useState({});
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [expandedModels, setExpandedModels] = useState(new Set());
  const [validationErrors, setValidationErrors] = useState([]);
  const [hasValidationErrors, setHasValidationErrors] = useState(false);
  const [showExcelModal, setShowExcelModal] = useState(false);
  
  // Estados para alertas de cambios significativos
  const [priceWarnings, setPriceWarnings] = useState([]);
  const [hasPriceWarnings, setHasPriceWarnings] = useState(false);
  const [totalAlerts, setTotalAlerts] = useState(0);
  
  // Estados para actualización de catálogos
  const [isUpdatingCatalog, setIsUpdatingCatalog] = useState(false);
  const [catalogToUpdate, setCatalogToUpdate] = useState('');
  const [catalogUpdateCategory, setCatalogUpdateCategory] = useState('');
  
  // Estados para edición de marca y modelo
  const [showEditManufacturerModal, setShowEditManufacturerModal] = useState(false);
  const [editingManufacturerModel, setEditingManufacturerModel] = useState(null);

  // Fetch inicial de catálogos
  useEffect(() => {
    fetchCatalogs();
  }, []);

  // Fetch de detalles (filtros y precios) cuando cambia el catálogo seleccionado
  useEffect(() => {
    if (selectedCatalog) {
      // Resetear filtros y precios antes de cargar nuevos datos
      setSelectedBrand('');
      setSelectedModel('');
      setSelectedConfig('');
      setBrands([]);
      setModels([]);
      setConfigs([]);
      setPrices([]);
      // Cargar datos iniciales para el catálogo seleccionado
      fetchCatalogDetails(selectedCatalog);
    } else {
        // Limpiar todo si no hay catálogo seleccionado
        setBrands([]);
        setModels([]);
        setConfigs([]);
        setPrices([]);
        setSelectedBrand('');
        setSelectedModel('');
        setSelectedConfig('');
    }
  }, [selectedCatalog]);

  const fetchCatalogs = async () => {
    setLoadingCatalogs(true);
    try {
      const resp = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/info',
        { headers: await getAuthHeaders(), params: { info_type: 'catalogs', tenant_id: tenantId }}
      );
      const rawCatalogs = resp.data.catalogs || [];
      setCatalogs(rawCatalogs);
      
      // Agrupar catálogos por categorías
      const grouped = {};
      rawCatalogs.forEach(catalogName => {
        // Verificar si el catálogo tiene el formato "categoria#nombre"
        if (catalogName.includes('#')) {
          const [category, name] = catalogName.split('#', 2);
          if (!grouped[category]) {
            grouped[category] = [];
          }
          grouped[category].push({
            fullName: catalogName,
            displayName: name
          });
        } else {
          // Si no tiene el formato, lo ponemos en una categoría "Sin categoría"
          const category = 'Sin categoría';
          if (!grouped[category]) {
            grouped[category] = [];
          }
          grouped[category].push({
            fullName: catalogName,
            displayName: catalogName
          });
        }
      });
      
      // Ordenar las categorías alfabéticamente y los catálogos dentro de cada categoría
      const sortedGrouped = {};
      Object.keys(grouped).sort().forEach(category => {
        sortedGrouped[category] = grouped[category].sort((a, b) => 
          a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' })
        );
      });
      
      setCatalogsByCategory(sortedGrouped);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingCatalogs(false);
    }
  };

  // Nueva función para cargar detalles del catálogo seleccionado (marcas y precios iniciales)
  const fetchCatalogDetails = async (catalog) => {
      setLoadingFilters(true); // Indicar carga de filtros
      setLoadingPrices(true); // Indicar carga de precios
      //console.log(`[fetchCatalogDetails] Iniciando carga para catálogo: ${catalog}`); // LOG 1
      try {
          // Fetch Marcas (para el filtro)
          const brandsResp = await axios.get(
              'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/info',
              { headers: await getAuthHeaders(), params: { info_type: 'catalog_brands', tenant_id: tenantId, catalog_name: catalog }}
          );
          // Ordenar alfabéticamente las marcas
          const fetchedBrands = (brandsResp.data.catalog_brands || []).sort((a, b) => 
              a.localeCompare(b, undefined, { sensitivity: 'base' })
          );
          //console.log('[fetchCatalogDetails] Marcas recibidas:', fetchedBrands); // LOG 2
          setBrands(fetchedBrands);

          // Fetch Precios (sin filtros inicialmente)
          const pricesResp = await axios.get(
              'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/info',
              { headers: await getAuthHeaders(), params: { info_type: 'prices_view', tenant_id: tenantId, catalog_name: catalog }}
          );
          //console.log('[fetchCatalogDetails] Respuesta raw de precios:', pricesResp.data); // LOG 3
          
          // Corregir aquí: usar prices con p minúscula
          const fetchedPrices = pricesResp.data.prices || []; // Usar prices
          //console.log('[fetchCatalogDetails] Precios extraídos (pricesResp.data.prices): ', fetchedPrices); // LOG 4 Actualizado
          setPrices(fetchedPrices);
          //console.log('[fetchCatalogDetails] Estado \'prices\' debería haberse actualizado.'); // LOG 5

      } catch (e) {
          console.error('[fetchCatalogDetails] Error fetching catalog details:', e); // LOG 6
          setMessage('Error al cargar detalles del catálogo.');
          setIsError(true);
          setBrands([]); // Limpiar en caso de error
          setPrices([]);
      } finally {
          setLoadingFilters(false);
          setLoadingPrices(false);
      }
  };

  // Funciones para cargar modelos y configs (se llaman al cambiar selects)
  const fetchModels = async (catalog, brand) => {
      if (!brand) { setModels([]); return; }
      setLoadingFilters(true);
      try {
          const resp = await axios.get(
              'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/info',
              { headers: await getAuthHeaders(), params: { info_type: 'catalog_models', tenant_id: tenantId, catalog_name: catalog, catalog_brand: brand }}
          );
          // Ordenar alfabéticamente los modelos
          const sortedModels = (resp.data.catalog_models || []).sort((a, b) => 
              a.localeCompare(b, undefined, { sensitivity: 'base' })
          );
          setModels(sortedModels);
      } catch (e) {
          console.error(e);
          setModels([]);
      } finally {
          setLoadingFilters(false);
      }
  };

  const fetchConfigs = async (catalog, brand, model) => {
      if (!model) { setConfigs([]); return; }
      setLoadingFilters(true);
      try {
          const resp = await axios.get(
              'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/info',
              { headers: await getAuthHeaders(), params: { info_type: 'catalog_configuration', tenant_id: tenantId, catalog_name: catalog, catalog_brand: brand, catalog_model: model }}
          );
          // Ordenar alfabéticamente las configuraciones
          const sortedConfigs = (resp.data.catalog_configuration || []).sort((a, b) => 
              a.localeCompare(b, undefined, { sensitivity: 'base' })
          );
          setConfigs(sortedConfigs);
      } catch (e) {
          console.error(e);
          setConfigs([]);
      } finally {
          setLoadingFilters(false);
      }
  };

  // Función para buscar precios con filtros aplicados (modo parcial con paginación)
  const fetchPricesWithFilters = async (filters = {}, page = 1) => {
    if (!selectedCatalog) return; // No buscar si no hay catálogo
    setLoadingPrices(true);
    
    try {
      // Extraer categoría del catálogo seleccionado
      const catalogCategory = selectedCatalog.includes('#') ? selectedCatalog.split('#')[0] : selectedCatalog;
      
      const params = {
        catalog_id: selectedCatalog,
        mode: 'partial',
        page: page,
        ...filters
      };
      
      const resp = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/prices',
        { headers: await getAuthHeaders(), params }
      );
      
      // Corregir: el backend devuelve los datos en 'items', no en 'data'
      const fetchedPrices = resp.data.items || [];
      const pagination = resp.data.pagination || {};
      
      setPrices(fetchedPrices);
      setCurrentPage(pagination.current_page || resp.data.page || 1);
      setTotalPages(pagination.total_pages || 1);
      setTotalRecords(pagination.total_records || fetchedPrices.length);
      setHasMorePages(pagination.has_next || false);
      
      if (fetchedPrices.length === 0) {
        setMessage('No se encontraron precios con los filtros aplicados.');
        setIsError(false);
      } else {
        setMessage('');
      }
    } catch (e) {
      console.error('[fetchPricesWithFilters] Error al buscar precios:', e);
      setMessage('Error al buscar precios.');
      setIsError(true);
      setPrices([]);
      setCurrentPage(1);
      setTotalPages(1);
      setTotalRecords(0);
      setHasMorePages(false);
    } finally {
      setLoadingPrices(false);
    }
  };

  // Al seleccionar catálogo (simplificado, la lógica principal está en useEffect)
  const handleSelectCatalog = (cat) => {
    if (cat === selectedCatalog) {
        // Opcional: deseleccionar si se vuelve a hacer clic
        // setSelectedCatalog(''); 
    } else {
        setSelectedCatalog(cat);
        // La carga de datos se maneja en el useEffect [selectedCatalog]
    }
  };

  // Función para expandir/contraer categorías
  const toggleCategory = (category) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  // Función para obtener categorías disponibles
  const fetchAvailableCategories = async () => {
    setLoadingCategories(true);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/categories',
        { headers }
      );
      setAvailableCategories(response.data || []);
    } catch (error) {
      console.error('Error al obtener categorías:', error);
      setMessage('Error al cargar las categorías.');
      setIsError(true);
    } finally {
      setLoadingCategories(false);
    }
  };

  // Función para crear una nueva categoría
  const createNewCategory = async (categoryName) => {
    setCreatingCategory(true);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/categories',
        { new_category: categoryName },
        { headers }
      );
      
      if (response.status === 201 || response.status === 200) {
        // Recargar categorías disponibles
        await fetchAvailableCategories();
        setMessage('Categoría creada exitosamente.');
        setIsError(false);
        setNewCategoryName('');
        return true;
      }
    } catch (error) {
      console.error('Error al crear categoría:', error);
      setMessage('Error al crear la nueva categoría.');
      setIsError(true);
      return false;
    } finally {
      setCreatingCategory(false);
    }
  };

  // Filter button
  const handleFilter = () => {
    // Construir filtros activos
    const filtersToApply = {};
    if (selectedBrand) filtersToApply.manufacturer = selectedBrand;
    if (selectedModel) filtersToApply.model = selectedModel;
    if (selectedConfig) filtersToApply.configuration = selectedConfig;
    
    // Resetear paginación al aplicar filtros
    setCurrentPage(1);
    fetchPricesWithFilters(filtersToApply, 1);
  };

  // Función para cambiar página
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      
      // Construir filtros activos
      const filtersToApply = {};
      if (selectedBrand) filtersToApply.manufacturer = selectedBrand;
      if (selectedModel) filtersToApply.model = selectedModel;
      if (selectedConfig) filtersToApply.configuration = selectedConfig;
      
      fetchPricesWithFilters(filtersToApply, newPage);
    }
  };

  // Clear cascada
  const clearBrand = () => { setSelectedBrand(''); setSelectedModel(''); setSelectedConfig(''); setModels([]); setConfigs([]); };
  const clearModel = () => { setSelectedModel(''); setSelectedConfig(''); setConfigs([]); };
  const clearConfig = () => { setSelectedConfig(''); };

  // --- Handlers para abrir modales ---
  const openCreateCatalogModal = async () => {
      setMessage(''); // Limpiar mensajes
      setUpdateSummary(null);
      
      // Cargar categorías disponibles
      await fetchAvailableCategories();
      
      // Mostrar modal de selección de categoría
      setSelectedCategory('');
      setShowCategoryModal(true);
  };

  const openUpdateCatalogModal = (catalogName) => {
      // Extraer categoría del nombre del catálogo
      const category = catalogName.includes('#') ? catalogName.split('#')[0] : 'Sin categoría';
      const displayName = catalogName.includes('#') ? catalogName.split('#')[1] : catalogName;
      
      // Configurar estados para actualización
      setIsUpdatingCatalog(true);
      setCatalogToUpdate(catalogName);
      setCatalogUpdateCategory(category);
      setSelectedCategory(category);
      
      // Limpiar mensajes y resetear procesamiento
      setMessage('');
      setUpdateSummary(null);
      resetExcelProcessing();
      
      // Abrir modal de Excel directamente (saltando selección de categoría)
      setShowExcelModal(true);
  };

  // Función para continuar con la creación del catálogo después de seleccionar categoría
  const handleCategorySelected = () => {
    if (!selectedCategory) {
      setMessage('Por favor, selecciona una categoría.');
      setIsError(true);
      return;
    }
    
    // Cerrar modal de categoría y abrir modal de Excel
    setShowCategoryModal(false);
    resetExcelProcessing();
    setShowExcelModal(true);
  };

  // Función para manejar la creación de nueva categoría en el modal
  const handleCreateNewCategoryInModal = async () => {
    if (!newCategoryName.trim()) {
      setMessage('Por favor, ingresa un nombre para la categoría.');
      setIsError(true);
      return;
    }
    
    const success = await createNewCategory(newCategoryName.trim());
    if (success) {
      setSelectedCategory(newCategoryName.trim());
      setNewCategoryName('');
    }
  };

  // === FUNCIONES PARA PROCESAMIENTO DE EXCEL ===

  // Función para resetear el estado del procesamiento de Excel
  const resetExcelProcessing = () => {
    setExcelProcessingStep('upload');
    setExcelWorkbook(null);
    setExcelSheets([]);
    setSelectedSheet('');
    setExcelHeaders([]);
    setExcelData([]);
    setColumnMapping({});
    setValidationData([]);
    setGroupedValidationData({});
    setExpandedGroups(new Set());
    setExpandedModels(new Set());
    setValidationErrors([]);
    setHasValidationErrors(false);
    setPriceWarnings([]);
    setHasPriceWarnings(false);
    setTotalAlerts(0);
  };

  // Función para recalcular cambios de precio para un registro específico
  const recalculatePriceChanges = (row) => {
    if (!row.existingRecord) {
      return {
        ...row,
        priceChanges: {},
        hasSignificantChange: false
      };
    }

    let priceChanges = {};
    let hasSignificantChange = false;

    ['grade_a', 'grade_b', 'grade_c'].forEach(grade => {
      const oldPrice = parseFloat(row.existingRecord[grade]) || 0;
      const newPrice = parseFloat(row.data[grade]) || 0;

      if (oldPrice > 0) {
        const percentChange = ((newPrice - oldPrice) / oldPrice) * 100;
        priceChanges[grade] = {
          oldPrice,
          newPrice,
          percentChange: Math.round(percentChange * 100) / 100,
          isSignificant: Math.abs(percentChange) > 50
        };

        if (Math.abs(percentChange) > 50) {
          hasSignificantChange = true;
        }
      } else {
        priceChanges[grade] = {
          oldPrice,
          newPrice,
          percentChange: newPrice > 0 ? 100 : 0,
          isSignificant: newPrice > 0
        };

        if (newPrice > 0) {
          hasSignificantChange = true;
        }
      }
    });

    return {
      ...row,
      priceChanges,
      hasSignificantChange
    };
  };

  // Función para actualizar datos inline
  const updateValidationData = (rowIndex, fieldKey, newValue) => {
    const updatedData = validationData.map(row => {
      if (row.rowIndex === rowIndex) {
        const cleanedValue = cleanAndFormatValue(newValue, fieldKey);
        const updatedRow = {
          ...row,
          data: {
            ...row.data,
            [fieldKey]: cleanedValue
          }
        };
        
        // Recalcular cambios de precio si es un campo de precio o si cambia manufacturer/model/configuration
        if (['grade_a', 'grade_b', 'grade_c', 'manufacturer', 'model', 'configuration'].includes(fieldKey)) {
          return recalculatePriceChanges(updatedRow);
        }
        
        return updatedRow;
      }
      return row;
    });
    
    setValidationData(updatedData);
    
    // Actualizar también los datos agrupados
    const newGroupedData = {};
    updatedData.forEach(row => {
      const manufacturer = row.data.manufacturer || 'Sin Marca';
      const model = row.data.model || 'Sin Modelo';
      
      if (!newGroupedData[manufacturer]) {
        newGroupedData[manufacturer] = {};
      }
      if (!newGroupedData[manufacturer][model]) {
        newGroupedData[manufacturer][model] = [];
      }
      newGroupedData[manufacturer][model].push(row);
    });
    
    setGroupedValidationData(newGroupedData);
    
    // Validar datos en tiempo real
    validateDataAndDetectErrors(updatedData);
  };

  // Función para alternar grupos expandidos
  const toggleGroup = (groupKey) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
    } else {
      newExpanded.add(groupKey);
    }
    setExpandedGroups(newExpanded);
  };

  // Función para alternar modelos expandidos
  const toggleModel = (modelKey) => {
    const newExpanded = new Set(expandedModels);
    if (newExpanded.has(modelKey)) {
      newExpanded.delete(modelKey);
    } else {
      newExpanded.add(modelKey);
    }
    setExpandedModels(newExpanded);
  };

  // Función para expandir múltiples grupos
  const expandMultipleGroups = (groupsSet) => {
    setExpandedGroups(groupsSet);
  };

  // Función para expandir múltiples modelos
  const expandMultipleModels = (modelsSet) => {
    setExpandedModels(modelsSet);
  };

  // Función para abrir el modal de edición de marca y modelo
  const openEditManufacturerModal = (manufacturer, model) => {
    setEditingManufacturerModel({ manufacturer, model });
    setShowEditManufacturerModal(true);
  };

  // Función para guardar cambios de marca y modelo
  const saveManufacturerModelChanges = async (newData) => {
    if (!editingManufacturerModel) return;

    const { manufacturer: oldManufacturer, model: oldModel } = editingManufacturerModel;
    const { manufacturer: newManufacturer, model: newModel } = newData;

    // Actualizar todos los registros que coincidan con la marca y modelo anterior
    const updatedData = validationData.map(row => {
      if (row.data.manufacturer === oldManufacturer && row.data.model === oldModel) {
        const updatedRow = {
          ...row,
          data: {
            ...row.data,
            manufacturer: newManufacturer,
            model: newModel
          }
        };
        
        // Recalcular cambios de precio con la nueva información
        return recalculatePriceChanges(updatedRow);
      }
      return row;
    });

    setValidationData(updatedData);

    // Actualizar también los datos agrupados
    const newGroupedData = {};
    updatedData.forEach(row => {
      const manufacturer = row.data.manufacturer || 'Sin Marca';
      const model = row.data.model || 'Sin Modelo';
      
      if (!newGroupedData[manufacturer]) {
        newGroupedData[manufacturer] = {};
      }
      if (!newGroupedData[manufacturer][model]) {
        newGroupedData[manufacturer][model] = [];
      }
      newGroupedData[manufacturer][model].push(row);
    });

    setGroupedValidationData(newGroupedData);

    // Actualizar grupos expandidos si es necesario
    const newExpandedGroups = new Set(expandedGroups);
    const newExpandedModels = new Set(expandedModels);
    
    // Si la marca cambió, actualizar los grupos expandidos
    if (oldManufacturer !== newManufacturer) {
      if (newExpandedGroups.has(oldManufacturer)) {
        newExpandedGroups.delete(oldManufacturer);
        newExpandedGroups.add(newManufacturer);
      }
    }
    
    // Actualizar modelos expandidos
    const oldModelKey = `${oldManufacturer}_${oldModel}`;
    const newModelKey = `${newManufacturer}_${newModel}`;
    if (newExpandedModels.has(oldModelKey)) {
      newExpandedModels.delete(oldModelKey);
      newExpandedModels.add(newModelKey);
    }

    setExpandedGroups(newExpandedGroups);
    setExpandedModels(newExpandedModels);

    // Validar datos en tiempo real
    validateDataAndDetectErrors(updatedData);

    // Limpiar estado de edición
    setEditingManufacturerModel(null);
  };

  // Funciones auxiliares para contadores
  const getRecordCounts = (records) => {
    const recordsToUpload = records.filter(r => hasPriceChanges(r));
    const newCount = recordsToUpload.filter(r => r.isNew).length;
    const updateCount = recordsToUpload.filter(r => !r.isNew).length;
    return { newCount, updateCount, total: recordsToUpload.length };
  };

  const getManufacturerCounts = (models) => {
    let totalNew = 0;
    let totalUpdate = 0;
    let totalRecords = 0;
    
    Object.values(models).forEach(records => {
      const counts = getRecordCounts(records);
      totalNew += counts.newCount;
      totalUpdate += counts.updateCount;
      totalRecords += counts.total;
    });
    
    return { newCount: totalNew, updateCount: totalUpdate, total: totalRecords };
  };

  const getTotalCounts = (groupedData) => {
    let totalNew = 0;
    let totalUpdate = 0;
    let totalRecords = 0;
    
    Object.values(groupedData).forEach(models => {
      const counts = getManufacturerCounts(models);
      totalNew += counts.newCount;
      totalUpdate += counts.updateCount;
      totalRecords += counts.total;
    });
    
    return { newCount: totalNew, updateCount: totalUpdate, total: totalRecords };
  };

  // Función para validar datos y detectar errores
  const validateDataAndDetectErrors = (data) => {
    const errors = [];
    const warnings = [];
    
    data.forEach(row => {
      const rowErrors = {};
      const rowWarnings = {};
      let hasRowError = false;
      let hasRowWarning = false;
      
      // Validar campos requeridos
      requiredPriceFields.forEach(field => {
        const value = row.data[field.key];
        
        if (!value || value.trim() === '') {
          rowErrors[field.key] = 'Campo requerido';
          hasRowError = true;
        } else if (['grade_a', 'grade_b', 'grade_c'].includes(field.key)) {
          // Validar precios
          const numValue = parseFloat(value);
          if (isNaN(numValue) || numValue < 0) {
            rowErrors[field.key] = 'Valor numérico inválido';
            hasRowError = true;
          }
        }
      });
      
      // Validar cambios significativos de precio (solo para actualizaciones)
      if (!row.isNew && row.priceChanges) {
        ['grade_a', 'grade_b', 'grade_c'].forEach(grade => {
          const change = row.priceChanges[grade];
          if (change && change.isSignificant) {
            const gradeLabel = grade.replace('grade_', 'Grado ').toUpperCase();
            const changeText = change.percentChange > 0 ? 'aumento' : 'disminución';
            rowWarnings[grade] = `${gradeLabel}: ${changeText} del ${Math.abs(change.percentChange)}% (${formatCurrency(change.oldPrice)} → ${formatCurrency(change.newPrice)})`;
            hasRowWarning = true;
          }
        });
      }
      
      if (hasRowError) {
        errors.push({
          rowIndex: row.rowIndex,
          errors: rowErrors,
          data: row.data
        });
      }
      
      if (hasRowWarning) {
        warnings.push({
          rowIndex: row.rowIndex,
          warnings: rowWarnings,
          data: row.data,
          manufacturer: row.data.manufacturer,
          model: row.data.model,
          configuration: row.data.configuration
        });
      }
    });
    
    setValidationErrors(errors);
    setHasValidationErrors(errors.length > 0);
    setPriceWarnings(warnings);
    setHasPriceWarnings(warnings.length > 0);
    setTotalAlerts(errors.length + warnings.length);
    
    return errors;
  };

  // Función auxiliar para verificar si un campo tiene error
  const hasFieldError = (rowIndex, fieldKey) => {
    const rowError = validationErrors.find(error => error.rowIndex === rowIndex);
    return rowError && rowError.errors[fieldKey];
  };

  // Función auxiliar para obtener mensaje de error de un campo
  const getFieldError = (rowIndex, fieldKey) => {
    const rowError = validationErrors.find(error => error.rowIndex === rowIndex);
    return rowError ? rowError.errors[fieldKey] : null;
  };

  // Función para verificar si una marca tiene errores
  const hasManufacturerErrors = (manufacturer) => {
    const manufacturerData = groupedValidationData[manufacturer];
    if (!manufacturerData) return false;
    
    return Object.values(manufacturerData).some(records => 
      records.some(record => 
        validationErrors.some(error => error.rowIndex === record.rowIndex)
      )
    );
  };

  // Función para verificar si un modelo tiene errores
  const hasModelErrors = (manufacturer, model) => {
    const modelData = groupedValidationData[manufacturer]?.[model];
    if (!modelData) return false;
    
    return modelData.some(record => 
      validationErrors.some(error => error.rowIndex === record.rowIndex)
    );
  };

  // Función para verificar si una marca tiene alertas de precio
  const hasManufacturerWarnings = (manufacturer) => {
    const manufacturerData = groupedValidationData[manufacturer];
    if (!manufacturerData) return false;
    
    return Object.values(manufacturerData).some(records => 
      records.some(record => 
        priceWarnings.some(warning => warning.rowIndex === record.rowIndex)
      )
    );
  };

  // Función para verificar si un modelo tiene alertas de precio
  const hasModelWarnings = (manufacturer, model) => {
    const modelData = groupedValidationData[manufacturer]?.[model];
    if (!modelData) return false;
    
    return modelData.some(record => 
      priceWarnings.some(warning => warning.rowIndex === record.rowIndex)
    );
  };

  // Función para verificar si un campo tiene alerta de precio
  const hasFieldWarning = (rowIndex, fieldKey) => {
    const rowWarning = priceWarnings.find(warning => warning.rowIndex === rowIndex);
    return rowWarning && rowWarning.warnings[fieldKey];
  };

  // Función para obtener mensaje de alerta de un campo
  const getFieldWarning = (rowIndex, fieldKey) => {
    const rowWarning = priceWarnings.find(warning => warning.rowIndex === rowIndex);
    return rowWarning ? rowWarning.warnings[fieldKey] : null;
  };

  // Función para verificar si una marca tiene registros que se subirán
  const hasManufacturerRecordsToUpload = (manufacturer) => {
    const manufacturerData = groupedValidationData[manufacturer];
    if (!manufacturerData) return false;
    
    return Object.values(manufacturerData).some(records => 
      records.some(record => hasPriceChanges(record))
    );
  };

  // Función para verificar si un modelo tiene registros que se subirán
  const hasModelRecordsToUpload = (manufacturer, model) => {
    const manufacturerData = groupedValidationData[manufacturer];
    if (!manufacturerData || !manufacturerData[model]) return false;
    
    return manufacturerData[model].some(record => hasPriceChanges(record));
  };

  // Función para verificar si un campo tiene valor cero
  const hasZeroValue = (rowIndex, fieldKey) => {
    const row = validationData.find(r => r.rowIndex === rowIndex);
    if (!row) return false;
    
    const value = parseFloat(row.data[fieldKey]) || 0;
    return value === 0;
  };

  // Función para verificar si un campo tiene cambio 0% y no es un registro nuevo
  const hasZeroChangeExisting = (rowIndex, fieldKey) => {
    const row = validationData.find(r => r.rowIndex === rowIndex);
    if (!row || row.isNew) return false;
    
    if (row.priceChanges && row.priceChanges[fieldKey]) {
      return row.priceChanges[fieldKey].percentChange === 0;
    }
    
    return false;
  };

  // Función para verificar duplicados con el catálogo actual (modo completo)
  const checkForDuplicates = async (validationRows) => {
    // Usar el catálogo que estamos actualizando o el seleccionado
    const catalogToCheck = isUpdatingCatalog ? catalogToUpdate : selectedCatalog;
    if (!catalogToCheck) return validationRows;
    
    try {
      // Obtener todos los precios del catálogo actual usando modo completo
      const headers = await getAuthHeaders();
      const params = {
        catalog_id: catalogToCheck,
        mode: 'complete'
      };
      
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/prices',
        { headers, params }
      );
      
      // Corregir: el backend devuelve los datos en 'items', no en 'data'
      const existingPrices = response.data.items || [];
      
      // Crear un mapa de registros existentes para búsqueda rápida
      const existingMap = new Map();
      existingPrices.forEach(price => {
        const key = `${price.manufacturer?.toUpperCase()}_${price.model?.toUpperCase()}_${price.configuration?.toUpperCase()}`;
        existingMap.set(key, price);
      });
      
      // Marcar registros como nuevos o actualizaciones y calcular porcentajes de cambio
      const updatedRows = validationRows.map(row => {
        const key = `${row.data.manufacturer?.toUpperCase()}_${row.data.model?.toUpperCase()}_${row.data.configuration?.toUpperCase()}`;
        const existingRecord = existingMap.get(key);
        
        let priceChanges = {};
        let hasSignificantChange = false;
        
        if (existingRecord) {
          // Calcular cambios porcentuales para cada grado
          ['grade_a', 'grade_b', 'grade_c'].forEach(grade => {
            const oldPrice = parseFloat(existingRecord[grade]) || 0;
            const newPrice = parseFloat(row.data[grade]) || 0;
            
            if (oldPrice > 0) {
              const percentChange = ((newPrice - oldPrice) / oldPrice) * 100;
              priceChanges[grade] = {
                oldPrice,
                newPrice,
                percentChange: Math.round(percentChange * 100) / 100,
                isSignificant: Math.abs(percentChange) > 50
              };
              
              if (Math.abs(percentChange) > 50) {
                hasSignificantChange = true;
              }
            } else {
              priceChanges[grade] = {
                oldPrice,
                newPrice,
                percentChange: newPrice > 0 ? 100 : 0,
                isSignificant: newPrice > 0
              };
              
              if (newPrice > 0) {
                hasSignificantChange = true;
              }
            }
          });
        }
        
        return {
          ...row,
          isNew: !existingRecord,
          existingRecord: existingRecord || null,
          priceChanges,
          hasSignificantChange
        };
      });
      
      return updatedRows;
    } catch (error) {
      console.error('Error verificando duplicados:', error);
      // En caso de error, asumir que todos son nuevos
      return validationRows.map(row => ({
        ...row,
        isNew: true,
        existingRecord: null,
        priceChanges: {},
        hasSignificantChange: false
      }));
    }
  };

  // Función para procesar archivo Excel
  const processExcelFile = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          
          // Validar número de hojas
          const sheetNames = workbook.SheetNames;
          if (sheetNames.length > 10) {
            reject(new Error('El archivo Excel no puede tener más de 10 hojas. Por favor, reduce el número de hojas.'));
            return;
          }
          
          setExcelWorkbook(workbook);
          setExcelSheets(sheetNames);
          
          if (sheetNames.length === 1) {
            // Si solo hay una hoja, procesarla automáticamente
            setSelectedSheet(sheetNames[0]);
            processSelectedSheet(workbook, sheetNames[0]);
            resolve();
          } else {
            // Si hay múltiples hojas, permitir al usuario seleccionar
            setExcelProcessingStep('sheet-selection');
            resolve();
          }
        } catch (error) {
          reject(new Error('Error al leer el archivo Excel. Asegúrate de que sea un archivo Excel válido.'));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Error al leer el archivo.'));
      };
      
      reader.readAsArrayBuffer(file);
    });
  };

  // Función para procesar la hoja seleccionada
  const processSelectedSheet = (workbook, sheetName) => {
    try {
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      
      if (jsonData.length === 0) {
        throw new Error('La hoja seleccionada está vacía.');
      }
      
      // Obtener headers (primera fila)
      const headers = jsonData[0].filter(header => header && header.toString().trim() !== '');
      
      if (headers.length === 0) {
        throw new Error('No se encontraron columnas válidas en la primera fila.');
      }
      
      // Obtener datos (filas restantes)
      const dataRows = jsonData.slice(1).filter(row => 
        row.some(cell => cell && cell.toString().trim() !== '')
      );
      
      if (dataRows.length === 0) {
        throw new Error('No se encontraron datos en la hoja seleccionada.');
      }
      
      setExcelHeaders(headers);
      setExcelData(dataRows);
      
      // Mapeo automático de columnas
      const autoMapping = performAutoMapping(headers);
      setColumnMapping(autoMapping);
      setExcelProcessingStep('column-mapping');
      
    } catch (error) {
      setMessage(`Error al procesar la hoja: ${error.message}`);
      setIsError(true);
    }
  };

  // Función para mapeo automático de columnas
  const performAutoMapping = (headers) => {
    const mapping = {};
    
    // Patrones de coincidencia para cada campo
    const patterns = {
      manufacturer: ['marca', 'brand', 'fabricante', 'manufacturer', 'empresa', 'compañia', 'compañía'],
      model: ['modelo', 'model', 'dispositivo', 'device', 'producto', 'product'],
      configuration: ['almacenamiento', 'storage', 'configuracion', 'configuración', 'config', 'capacidad', 'gb', 'memoria'],
      grade_a: ['grado_a', 'gradoa', 'grade_a', 'gradea', 'a', 'valor_a', 'valora', 'precio_a', 'precioa'],
      grade_b: ['grado_b', 'gradob', 'grade_b', 'gradeb', 'b', 'valor_b', 'valorb', 'precio_b', 'preciob'],
      grade_c: ['grado_c', 'gradoc', 'grade_c', 'gradec', 'c', 'valor_c', 'valorc', 'precio_c', 'precioc']
    };
    
    headers.forEach((header, index) => {
      const headerLower = header.toString().toLowerCase().trim();
      
      for (const [fieldKey, patternList] of Object.entries(patterns)) {
        if (patternList.some(pattern => headerLower.includes(pattern))) {
          if (!mapping[fieldKey]) { // Solo mapear si no está ya mapeado
            mapping[fieldKey] = index;
          }
        }
      }
    });
    
    return mapping;
  };

  // Función para manejar la selección de hoja
  const handleSheetSelection = (sheetName) => {
    setSelectedSheet(sheetName);
    processSelectedSheet(excelWorkbook, sheetName);
  };

  // Función para actualizar mapeo de columnas
  const updateColumnMapping = (fieldKey, columnIndex) => {
    const newMapping = { ...columnMapping };
    
    // Convertir a número si es string
    const numericIndex = columnIndex === '' ? null : parseInt(columnIndex);
    
    // Remover mapeo previo de esta columna si existe
    if (numericIndex !== null) {
      Object.keys(newMapping).forEach(key => {
        if (newMapping[key] === numericIndex) {
          delete newMapping[key];
        }
      });
    }
    
    // Agregar nuevo mapeo o eliminar si es vacío
    if (numericIndex !== null && !isNaN(numericIndex)) {
      newMapping[fieldKey] = numericIndex;
    } else {
      delete newMapping[fieldKey];
    }
    
    setColumnMapping(newMapping);
  };

  // Verificar si el mapeo está completo
  const isMappingComplete = () => {
    return requiredPriceFields.every(field => columnMapping[field.key] !== undefined);
  };

  // Función para manejar la carga de archivo Excel
  const handleExcelFileUpload = async (file) => {
    if (!file) return;
    
    // Validar tipo de archivo
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ];
    
    if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls)$/i)) {
      setMessage('Por favor selecciona un archivo Excel válido (.xlsx o .xls)');
      setIsError(true);
      return;
    }
    
    setMessage('Procesando archivo Excel...');
    setIsError(false);
    
    try {
      await processExcelFile(file);
    } catch (error) {
      setMessage(`Error al procesar archivo: ${error.message}`);
      setIsError(true);
      resetExcelProcessing();
    }
  };

  // Función para proceder a la validación de datos
  const proceedToDataValidation = async () => {
    if (!isMappingComplete()) {
      setMessage('Por favor, mapea todas las columnas requeridas antes de continuar.');
      setIsError(true);
      
      // Agregar clase de error a campos faltantes
      const missingFields = requiredPriceFields.filter(field => columnMapping[field.key] === undefined);
      missingFields.forEach(field => {
        const selectElement = document.querySelector(`select[data-field="${field.key}"]`);
        if (selectElement) {
          selectElement.classList.add('mapping-error');
          setTimeout(() => {
            selectElement.classList.remove('mapping-error');
          }, 5000);
        }
      });
      
      return;
    }
    
    setMessage('Procesando datos y verificando duplicados...');
    setIsError(false);
    
    try {
      // Preparar datos para validación con limpieza
      const validationRows = excelData.map((row, index) => {
        const mappedData = {};
        requiredPriceFields.forEach(field => {
          const columnIndex = columnMapping[field.key];
          if (columnIndex !== undefined) {
            const rawValue = row[columnIndex] || '';
            mappedData[field.key] = cleanAndFormatValue(rawValue, field.key);
          }
        });
        return {
          rowIndex: index + 2, // +2 porque empezamos desde la fila 2 (después del header)
          data: mappedData,
          errors: {},
          isNew: true // Por defecto, asumimos que todos son nuevos
        };
      });
      
      // Verificar duplicados
      const checkedRows = await checkForDuplicates(validationRows);
      
      // Agrupar por marca y modelo
      const groupedData = {};
      checkedRows.forEach(row => {
        const manufacturer = row.data.manufacturer || 'Sin Marca';
        const model = row.data.model || 'Sin Modelo';
        
        if (!groupedData[manufacturer]) {
          groupedData[manufacturer] = {};
        }
        if (!groupedData[manufacturer][model]) {
          groupedData[manufacturer][model] = [];
        }
        groupedData[manufacturer][model].push(row);
      });
      
      setValidationData(checkedRows);
      setGroupedValidationData(groupedData);
      
      // Validar datos inicialmente
      validateDataAndDetectErrors(checkedRows);
      
      // Expandir automáticamente el primer grupo
      const firstManufacturer = Object.keys(groupedData)[0];
      if (firstManufacturer) {
        setExpandedGroups(new Set([firstManufacturer]));
      }
      
      setExcelProcessingStep('data-validation');
      setMessage('');
      
    } catch (error) {
      console.error('Error al procesar datos:', error);
      setMessage('Error al procesar los datos. Inténtalo de nuevo.');
      setIsError(true);
    }
  };

  // Función para manejar cambios en inputs numéricos preservando posición del cursor
  const handleNumericInputChange = (e, rowIndex, fieldKey, onUpdateValidationData) => {
    const input = e.target;
    const cursorPosition = input.selectionStart;
    const value = input.value;
    
    // Remover todo excepto dígitos, puntos y comas
    let cleanValue = value.replace(/[^\d.,]/g, '');
    
    // Manejar formato de números
    if (cleanValue.includes('.') && cleanValue.includes(',')) {
      // Si tiene ambos, el último es el decimal
      const lastCommaIndex = cleanValue.lastIndexOf(',');
      const lastDotIndex = cleanValue.lastIndexOf('.');
      if (lastCommaIndex > lastDotIndex) {
        // La coma es el decimal
        cleanValue = cleanValue.replace(/\./g, '').replace(',', '.');
      } else {
        // El punto es el decimal
        cleanValue = cleanValue.replace(/,/g, '');
      }
    } else if (cleanValue.includes(',')) {
      // Solo coma, convertir a punto
      cleanValue = cleanValue.replace(',', '.');
    }
    
    // Actualizar el valor
    onUpdateValidationData(rowIndex, fieldKey, cleanValue);
    
    // Preservar posición del cursor después del próximo render
    setTimeout(() => {
      if (input && document.activeElement === input) {
        // Calcular nueva posición del cursor basada en el valor formateado
        const newValue = input.value;
        const lengthDiff = newValue.length - value.length;
        const newCursorPosition = Math.max(0, Math.min(newValue.length, cursorPosition + lengthDiff));
        input.setSelectionRange(newCursorPosition, newCursorPosition);
      }
    }, 0);
  };

  // Función para validar y limpiar datos
  const cleanAndFormatValue = (value, fieldKey) => {
    if (value === null || value === undefined) {
      return '';
    }
    
    let cleanValue = value.toString().trim();
    
    // Manejar valores especiales que deberían ser cero
    if (['grade_a', 'grade_b', 'grade_c'].includes(fieldKey)) {
      // Si el valor está vacío, es un guión, o es "$ -", convertir a cero
      if (!cleanValue || cleanValue === '' || cleanValue === '-' || cleanValue === '$ -' || cleanValue === '$-') {
        return '0';
      }
    }
    
    // Remover tildes de todos los campos de texto
    const removeAccents = (str) => {
      return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    };
    
    // Convertir a mayúsculas y remover tildes para campos específicos
    if (['manufacturer', 'model', 'configuration'].includes(fieldKey)) {
      cleanValue = removeAccents(cleanValue.toUpperCase());
    }
    
    // Validar y limpiar precios
    if (['grade_a', 'grade_b', 'grade_c'].includes(fieldKey)) {
      // Remover caracteres no numéricos excepto punto y coma
      cleanValue = cleanValue.replace(/[^\d.,]/g, '');
      // Convertir coma a punto para decimales
      cleanValue = cleanValue.replace(',', '.');
      // Validar que sea un número válido
      const numValue = parseFloat(cleanValue);
      if (isNaN(numValue) || numValue < 0) {
        return '';
      }
      return numValue.toString();
    }
    
    return cleanValue;
  };

  // Función para verificar si un registro tiene cambios en los precios
  const hasPriceChanges = (row) => {
    if (row.isNew) return true; // Los registros nuevos siempre se envían
    
    if (!row.priceChanges) return false;
    
    // Verificar si hay cambios en algún grado
    return ['grade_a', 'grade_b', 'grade_c'].some(grade => {
      const change = row.priceChanges[grade];
      if (!change) return false;
      
      // Comparar valores con tolerancia para decimales
      const oldPrice = parseFloat(change.oldPrice) || 0;
      const newPrice = parseFloat(change.newPrice) || 0;
      
      return Math.abs(oldPrice - newPrice) > 0.01; // Tolerancia de 1 centavo
    });
  };

  // Función para finalizar y enviar datos al servidor
  const finalizeExcelUpload = async (catalogName) => {
    // Para actualizaciones, usar el nombre del catálogo existente
    const finalCatalogName = isUpdatingCatalog 
      ? (catalogToUpdate.includes('#') ? catalogToUpdate.split('#')[1] : catalogToUpdate)
      : catalogName.trim();
    
    if (!finalCatalogName) {
      setMessage('Por favor, ingresa un nombre para el catálogo.');
      setIsError(true);
      return;
    }
    
    try {
      // Filtrar solo los registros que tienen cambios
      const changedRecords = validationData.filter(row => hasPriceChanges(row));
      
      if (changedRecords.length === 0) {
        setMessage('No se detectaron cambios en los precios. No hay nada que actualizar.');
        setIsError(false);
        return;
      }
      
      // Preparar datos en el formato JSON que espera la nueva API
      const items = changedRecords.map(row => {
        const cleanedData = {};
        
        // Mapear campos requeridos
        requiredPriceFields.forEach(field => {
          cleanedData[field.key] = cleanAndFormatValue(row.data[field.key], field.key);
        });
        
        // Generar device_id único basado en manufacturer, model y configuration
        const deviceId = `${cleanedData.manufacturer}_${cleanedData.model}_${cleanedData.configuration}`.replace(/\s+/g, '_').toUpperCase();
        
        // Generar nombres de dispositivo
        const deviceName = `${cleanedData.model} ${cleanedData.configuration}`;
        const deviceFullName = `${cleanedData.manufacturer} ${cleanedData.model} ${cleanedData.configuration}`;
        
        return {
          device_id: deviceId,
          manufacturer: cleanedData.manufacturer,
          model: cleanedData.model,
          configuration: cleanedData.configuration,
          grade_a: parseFloat(cleanedData.grade_a) || 0,
          grade_b: parseFloat(cleanedData.grade_b) || 0,
          grade_c: parseFloat(cleanedData.grade_c) || 0,
          device_name: deviceName,
          device_full_name: deviceFullName
        };
      });
      
      // Mostrar información sobre la optimización
      const totalRecords = validationData.length;
      const recordsToSend = items.length;
      const recordsSkipped = totalRecords - recordsToSend;
      
      setMessage(`Optimización aplicada: Enviando ${recordsToSend} de ${totalRecords} registros (${recordsSkipped} sin cambios omitidos).`);
      setIsError(false);
      
      // Preparar datos para la nueva API
      const data = {
        catalog_category: selectedCategory,
        catalog_id: isUpdatingCatalog ? catalogToUpdate : `${selectedCategory}#${catalogName.trim()}`,
        items: items
      };
      
      await handleCatalogCreateOrUpdateJSON(data);
      
      // Cerrar modal y resetear estados
      setShowExcelModal(false);
      resetExcelProcessing();
      
      // Limpiar estados de actualización
      setIsUpdatingCatalog(false);
      setCatalogToUpdate('');
      setCatalogUpdateCategory('');
      
    } catch (error) {
      console.error('Error al finalizar carga:', error);
      setMessage('Error al procesar los datos. Por favor, inténtalo de nuevo.');
      setIsError(true);
    }
  };

  // Crear o actualizar catálogo (método original para Excel - CONVERTIR A JSON)
  const handleCatalogCreateOrUpdate = async (data) => {
    setMessage(''); 
    setUpdateSummary(null);
    try {
      // Si es creación, agregar la categoría al nombre del catálogo
      if (catalogModalMode === 'create' && selectedCategory) {
        data.catalog_id = `${selectedCategory}#${data.catalog_id}`;
      }
      
      // Convertir datos de Excel a formato JSON
      if (data.excel_query) {
        // Decodificar Excel y convertir a JSON
        const excelData = await convertExcelToJSON(data.excel_query, data.catalog_id);
        
        // Preparar datos para la nueva API
        const jsonData = {
          catalog_category: selectedCategory || data.catalog_id.split('#')[0],
          catalog_id: data.catalog_id,
          items: excelData
        };
        
        await handleCatalogCreateOrUpdateJSON(jsonData);
        return;
      }
      
      // Si no hay excel_query, usar el método original (no debería llegar aquí)
      const resp = await axios.post(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/prices',
        data,
        { headers: { ...await getAuthHeaders(), 'Content-Type': 'application/json' }}
      );
      
      // Refrescar listados y estado
      await fetchCatalogs();
      // Si se actualizó el catálogo seleccionado, refrescar detalles
      if (catalogModalMode === 'update' && data.catalog_id === selectedCatalog) {
          fetchCatalogDetails(selectedCatalog); 
      }
      
      setMessage(catalogModalMode === 'create'
        ? 'Catálogo creado correctamente.'
        : 'Catálogo actualizado correctamente.'
      );
      setIsError(false);
      setUpdateSummary({ exitosos: resp.data.registros_exitosos, errores: resp.data.registros_con_errores });
    } catch (e) {
      console.error(e);
      setMessage(`Error al ${catalogModalMode === 'create' ? 'crear' : 'actualizar'} catálogo: ${e.response?.data?.error || e.response?.data?.message || e.message}`);
      setIsError(true);
      setUpdateSummary(null);
    }
  };

  // Función para convertir datos de Excel a formato JSON
  const convertExcelToJSON = async (excelBase64, catalogId) => {
    try {
      // Decodificar base64 a buffer
      const binaryString = atob(excelBase64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      // Leer Excel
      const workbook = XLSX.read(bytes, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      
      // Obtener headers y datos
      const headers = jsonData[0];
      const dataRows = jsonData.slice(1);
      
      // Mapear campos
      const fieldMapping = {};
      headers.forEach((header, index) => {
        const headerLower = header.toString().toLowerCase().trim();
        if (headerLower.includes('manufacturer') || headerLower.includes('marca')) {
          fieldMapping.manufacturer = index;
        } else if (headerLower.includes('model') || headerLower.includes('modelo')) {
          fieldMapping.model = index;
        } else if (headerLower.includes('configuration') || headerLower.includes('almacenamiento')) {
          fieldMapping.configuration = index;
        } else if (headerLower.includes('grade_a') || headerLower.includes('grado_a')) {
          fieldMapping.grade_a = index;
        } else if (headerLower.includes('grade_b') || headerLower.includes('grado_b')) {
          fieldMapping.grade_b = index;
        } else if (headerLower.includes('grade_c') || headerLower.includes('grado_c')) {
          fieldMapping.grade_c = index;
        }
      });
      
      // Convertir datos
      const items = dataRows.map(row => {
        const manufacturer = row[fieldMapping.manufacturer] || '';
        const model = row[fieldMapping.model] || '';
        const configuration = row[fieldMapping.configuration] || '';
        
        // Generar device_id único
        const deviceId = `${manufacturer}_${model}_${configuration}`.replace(/\s+/g, '_').toUpperCase();
        
        return {
          device_id: deviceId,
          manufacturer: manufacturer,
          model: model,
          configuration: configuration,
          grade_a: parseFloat(row[fieldMapping.grade_a]) || 0,
          grade_b: parseFloat(row[fieldMapping.grade_b]) || 0,
          grade_c: parseFloat(row[fieldMapping.grade_c]) || 0,
          device_name: `${model} ${configuration}`,
          device_full_name: `${manufacturer} ${model} ${configuration}`
        };
      });
      
      return items;
    } catch (error) {
      console.error('Error converting Excel to JSON:', error);
      throw error;
    }
  };

  // Crear o actualizar catálogo (método nuevo para JSON)
  const handleCatalogCreateOrUpdateJSON = async (data) => {
    setMessage(''); 
    setUpdateSummary(null);
    try {
      const resp = await axios.post(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/prices',
        data,
        { headers: { ...await getAuthHeaders(), 'Content-Type': 'application/json' }}
      );
      
      // Refrescar listados y estado
      await fetchCatalogs();
      
      // Si se creó uno nuevo, seleccionarlo automáticamente
      // Si se actualizó, mantener el catálogo seleccionado o seleccionar el actualizado
      if (isUpdatingCatalog) {
        setSelectedCatalog(data.catalog_id);
        setMessage('Catálogo actualizado correctamente.');
      } else {
        setSelectedCatalog(data.catalog_id);
        setMessage('Catálogo creado correctamente.');
      }
      setIsError(false);
      setUpdateSummary({ 
        exitosos: resp.data.registros_exitosos, 
        errores: resp.data.registros_con_errores 
      });
    } catch (e) {
      console.error(e);
      setMessage(`Error al crear catálogo: ${e.response?.data?.error || e.response?.data?.message || e.message}`);
      setIsError(true);
      setUpdateSummary(null);
    }
  };

  // --- Funciones auxiliares ---
  const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(Number(value))) {
        return '-'; // Retornar guion si no es un número válido
    }
    try {
        // Formatear con puntos como separador de miles y comas para decimales
        const numValue = Number(value);
        return numValue.toLocaleString('es-CO', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    } catch (error) {
        console.error("Error formateando moneda:", error, "Valor:", value);
        return String(value); // Devolver el valor original si falla el formato
    }
  };

  // --- Renderizado --- 
  return (
    <div className="precios-container">
      <header className="header">
        <Logo 
          variant="horizontal" 
          size="medium" 
          onClick={() => navigate('/dashboard')} 
        />
        <div className="header-title">Actualización de Precios</div>
        <div className="user-menu">
          <span>Hola, {userData.user_name || 'Usuario'}</span>
          <button className="btn btn-outline" onClick={() => navigate('/dashboard')}>Volver al Dashboard</button>
        </div>
      </header>
      
      {/* Layout Principal con Grid */} 
      <div className="precios-layout container">
        {/* Columna Izquierda: Lista de Catálogos */}
        <div className="catalog-list-column card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 className="column-title">Catálogos</h3>
            <button className="btn btn-primary btn-sm" onClick={openCreateCatalogModal}>
              + Nuevo
            </button>
          </div>
          {loadingCatalogs ? <div className="loading-spinner-small"></div> : Object.keys(catalogsByCategory).length === 0 ? (
              <p className="empty-state">No hay catálogos.</p>
          ) : (
            <div className="catalog-categories">
              {Object.entries(catalogsByCategory).map(([category, catalogList]) => (
                <div key={category} className="category-group">
                  <div 
                    className={`category-header ${expandedCategories.has(category) ? 'expanded' : ''}`}
                    onClick={() => toggleCategory(category)}
                  >
                    <div className="category-info">
                      <span className={`expand-icon ${expandedCategories.has(category) ? 'rotated' : ''}`}>
                        📁
                      </span>
                      <span className="category-name">{category}</span>
                      <span className="category-count">({catalogList.length})</span>
                    </div>
                  </div>
                  
                  {expandedCategories.has(category) && (
                    <ul className="catalog-list">
                      {catalogList.map(catalog => (
                        <li
                          key={catalog.fullName}
                          className={`catalog-list-item ${selectedCatalog === catalog.fullName ? 'active' : ''}`}
                          onClick={() => handleSelectCatalog(catalog.fullName)}
                        >
                          <span className="item-text">{catalog.displayName}</span>
                          <div className="item-actions">
                              <button 
                                  className="btn-icon" 
                                  title="Actualizar catálogo" 
                                  onClick={e => { e.stopPropagation(); openUpdateCatalogModal(catalog.fullName); }}
                              >
                                  ✏️
                              </button>
                              {/* Aquí podría ir un botón de eliminar catálogo si se implementa */}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Columna Derecha: Detalles y Precios */}
        <div className="details-column card">
          {!selectedCatalog ? (
            <div className="empty-state-details">
              <span className="icon-lg">👈</span>
              <h3>Selecciona un catálogo</h3>
              <p>Haz clic en un catálogo de la lista para ver o administrar sus precios.</p>
            </div>
          ) : (
            <div className="catalog-details">
              <h2 className="selected-catalog-title">{selectedCatalog}</h2>
              
              {/* Filtros */} 
              <div className="filter-section">
                  <h4 className="filter-title">Filtrar Precios</h4>
                  {loadingFilters ? <div className="loading-spinner-small" style={{margin: '1rem 0'}}></div> : (
                      <div className="filter-row">
                          <div className="form-group filter-group">
                              <label>Marca</label>
                              <Select
                                  value={selectedBrand ? { value: selectedBrand, label: selectedBrand } : null}
                                  onChange={(option) => {
                                      const value = option ? option.value : '';
                                      setSelectedBrand(value);
                                      setSelectedModel('');
                                      setSelectedConfig('');
                                      setModels([]);
                                      setConfigs([]);
                                      if(value) fetchModels(selectedCatalog, value);
                                  }}
                                  options={[
                                      { value: '', label: 'Todas' },
                                      ...brands.map(b => ({ value: b, label: b }))
                                  ]}
                                  isDisabled={brands.length === 0}
                                  placeholder="Seleccionar marca..."
                                  isClearable={true}
                                  className="react-select-container"
                                  classNamePrefix="react-select"
                                  isSearchable={true}
                                  menuPortalTarget={document.body}
                                  styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                              />
                          </div>
                          <div className="form-group filter-group">
                              <label>Modelo</label>
                              <Select
                                  value={selectedModel ? { value: selectedModel, label: selectedModel } : null}
                                  onChange={(option) => {
                                      const value = option ? option.value : '';
                                      setSelectedModel(value);
                                      setSelectedConfig('');
                                      setConfigs([]);
                                      if(value) fetchConfigs(selectedCatalog, selectedBrand, value);
                                  }}
                                  options={[
                                      { value: '', label: 'Todos' },
                                      ...models.map(m => ({ value: m, label: m }))
                                  ]}
                                  isDisabled={!selectedBrand || models.length === 0}
                                  placeholder="Seleccionar modelo..."
                                  isClearable={true}
                                  className="react-select-container"
                                  classNamePrefix="react-select"
                                  isSearchable={true}
                                  menuPortalTarget={document.body}
                                  styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                              />
                          </div>
                          <div className="form-group filter-group">
                              <label>Almacenamiento</label>
                              <Select
                                  value={selectedConfig ? { value: selectedConfig, label: selectedConfig } : null}
                                  onChange={(option) => {
                                      setSelectedConfig(option ? option.value : '');
                                  }}
                                  options={[
                                      { value: '', label: 'Todos' },
                                      ...configs.map(c => ({ value: c, label: c }))
                                  ]}
                                  isDisabled={!selectedModel || configs.length === 0}
                                  placeholder="Seleccionar almacenamiento..."
                                  isClearable={true}
                                  className="react-select-container"
                                  classNamePrefix="react-select"
                                  isSearchable={true}
                                  menuPortalTarget={document.body}
                                  styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                              />
                          </div>
                          <button className="btn btn-secondary filter-apply-btn" onClick={handleFilter} disabled={loadingPrices}> 
                              Aplicar Filtros
                          </button>
                      </div>
                  )}
              </div>
              
              {/* Mensaje y Resumen */}
              {message && <div className={`message ${isError ? 'message-error' : 'message-success'}`}>{message}</div>}
              {updateSummary && (
                  <div className="update-summary message message-info">
                      Registros Exitosos: {updateSummary.exitosos ?? 'N/A'} | Registros con Errores: {updateSummary.errores ?? 'N/A'}
                </div>
              )}

              {/* Tabla de precios */} 
              <div className="table-container">
                  <div className="table-header">
                    <h4 className="table-title">Precios Actuales</h4>
                    {totalRecords > 0 && (
                      <div className="table-info">
                        <span className="records-count">
                          {totalRecords} registros total
                        </span>
                        <span className="page-info">
                          Página {currentPage} de {totalPages}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {loadingPrices ? <div className="loading-spinner-small" style={{margin: '2rem auto'}}></div> : prices.length === 0 && !message ? (
                      <p className="empty-state-small">No hay precios para mostrar. Aplica filtros o verifica el catálogo.</p>
                  ) : prices.length > 0 ? (
                      <>
                        <div className="table-responsive">
                            <table className="transactions-table prices-table"> 
                                <thead>
                                    <tr>
                                        <th>Dispositivo</th>
                                        <th>Marca</th>
                                        <th>Almacenamiento</th>
                                        <th>Grado A</th>
                                        <th>Grado B</th>
                                        <th>Grado C</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {prices.map((p) => (
                                        <tr key={p.device_id || p.device_full_name}> {/* Usar un ID único si es posible */}
                                            <td>{p.device_name ?? '-'}</td>
                                            <td>{p.manufacturer ?? '-'}</td> {/* Nueva columna Marca */} 
                                            <td>{p.configuration ?? '-'}</td> {/* Nueva columna Almacenamiento */} 
                                            {/* Aplicar formato de moneda */}
                                            <td>{formatCurrency(p.grade_a)}</td> 
                                            <td>{formatCurrency(p.grade_b)}</td>
                                            <td>{formatCurrency(p.grade_c)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        
                        {/* Controles de paginación */}
                        {totalPages > 1 && (
                          <div className="pagination-controls">
                            <button 
                              className="btn btn-outline btn-sm"
                              onClick={() => handlePageChange(currentPage - 1)}
                              disabled={currentPage <= 1}
                            >
                              ← Anterior
                            </button>
                            
                            <div className="page-numbers">
                              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                let pageNum;
                                if (totalPages <= 5) {
                                  pageNum = i + 1;
                                } else if (currentPage <= 3) {
                                  pageNum = i + 1;
                                } else if (currentPage >= totalPages - 2) {
                                  pageNum = totalPages - 4 + i;
                                } else {
                                  pageNum = currentPage - 2 + i;
                                }
                                
                                return (
                                  <button
                                    key={pageNum}
                                    className={`btn btn-sm ${currentPage === pageNum ? 'btn-primary' : 'btn-outline'}`}
                                    onClick={() => handlePageChange(pageNum)}
                                  >
                                    {pageNum}
                                  </button>
                                );
                              })}
                            </div>
                            
                            <button 
                              className="btn btn-outline btn-sm"
                              onClick={() => handlePageChange(currentPage + 1)}
                              disabled={currentPage >= totalPages}
                            >
                              Siguiente →
                            </button>
                          </div>
                        )}
                      </>
                  ) : null /* No mostrar nada si hay mensaje de error o 'no resultados' */}
            </div>
              
            </div> // Cierre de catalog-details
          )}
        </div>
      </div>

      {/* Modal Seleccionar Categoría */}
      <CategorySelectionModal
        isOpen={showCategoryModal}
        categories={availableCategories}
        selectedCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
        onClose={() => setShowCategoryModal(false)}
        onContinue={handleCategorySelected}
        onCreateCategory={handleCreateNewCategoryInModal}
        newCategoryName={newCategoryName}
        onNewCategoryNameChange={setNewCategoryName}
        creatingCategory={creatingCategory}
        loadingCategories={loadingCategories}
      />

      {/* Modal Procesamiento de Excel */}
      <ExcelProcessingModal
        isOpen={showExcelModal}
        onClose={() => {
          setShowExcelModal(false);
          // Limpiar estados de actualización al cerrar
          setIsUpdatingCatalog(false);
          setCatalogToUpdate('');
          setCatalogUpdateCategory('');
        }}
        step={excelProcessingStep}
        sheets={excelSheets}
        selectedSheet={selectedSheet}
        onSheetSelect={handleSheetSelection}
        headers={excelHeaders}
        data={excelData}
        columnMapping={columnMapping}
        onColumnMappingUpdate={updateColumnMapping}
        requiredFields={requiredPriceFields}
        isMappingComplete={isMappingComplete}
        onProceedToValidation={proceedToDataValidation}
        validationData={validationData}
        groupedValidationData={groupedValidationData}
        expandedGroups={expandedGroups}
        expandedModels={expandedModels}
        onToggleGroup={toggleGroup}
        onToggleModel={toggleModel}
        onExpandMultipleGroups={expandMultipleGroups}
        onExpandMultipleModels={expandMultipleModels}
        onUpdateValidationData={updateValidationData}
        getRecordCounts={getRecordCounts}
        getManufacturerCounts={getManufacturerCounts}
        getTotalCounts={getTotalCounts}
        validationErrors={validationErrors}
        hasValidationErrors={hasValidationErrors}
        hasFieldError={hasFieldError}
        getFieldError={getFieldError}
        hasManufacturerErrors={hasManufacturerErrors}
        hasModelErrors={hasModelErrors}
        hasManufacturerWarnings={hasManufacturerWarnings}
        hasModelWarnings={hasModelWarnings}
        hasFieldWarning={hasFieldWarning}
        getFieldWarning={getFieldWarning}
        hasPriceChanges={hasPriceChanges}
        priceWarnings={priceWarnings}
        hasPriceWarnings={hasPriceWarnings}
        totalAlerts={totalAlerts}
        onFinalize={finalizeExcelUpload}
        onFileUpload={handleExcelFileUpload}
        onBackToMapping={() => setExcelProcessingStep('column-mapping')}
        message={message}
        isError={isError}
        isUpdatingCatalog={isUpdatingCatalog}
        catalogToUpdate={catalogToUpdate}
        catalogUpdateCategory={catalogUpdateCategory}
        handleNumericInputChange={handleNumericInputChange}
        openEditManufacturerModal={openEditManufacturerModal}
        hasZeroValue={hasZeroValue}
        formatCurrency={formatCurrency}
        hasManufacturerRecordsToUpload={hasManufacturerRecordsToUpload}
        hasModelRecordsToUpload={hasModelRecordsToUpload}
        hasZeroChangeExisting={hasZeroChangeExisting}
      />

      {/* Modal Editar Marca y Modelo */}
      <EditManufacturerModelModal
        isOpen={showEditManufacturerModal}
        onClose={() => {
          setShowEditManufacturerModal(false);
          setEditingManufacturerModel(null);
        }}
        onSave={saveManufacturerModelChanges}
        initialData={editingManufacturerModel}
      />

      {/* Modal Crear/Actualizar Catálogo */} 
      <CatalogModal
        isOpen={isCatalogModalOpen}
        mode={catalogModalMode}
        initialName={editingCatalogName} // Usar estado para el nombre inicial
        onClose={()=>setIsCatalogModalOpen(false)}
        onSubmit={handleCatalogCreateOrUpdate}
      />
      
      {/* Estilos JSX */} 
      <style jsx>{`
          /* Estilos generales */
          .precios-container {
              /* Puedes añadir estilos generales si es necesario */
          }
          .card {
              background-color: #fff;
              border-radius: 8px;
              padding: 1.5rem;
              box-shadow: 0 2px 4px rgba(0,0,0,0.05);
              overflow: hidden; /* Para contener bordes y sombras */
          }
          
          /* Layout Principal */
          .precios-layout {
              display: grid;
              grid-template-columns: 320px 1fr; /* Columna izquierda más ancha, derecha flexible */
              gap: 2rem; /* Mayor separación entre columnas */
              align-items: start; /* Alinea las columnas en la parte superior */
              max-width: 100%;
              overflow-x: hidden; /* Prevenir desbordamiento horizontal */
          }

          /* Columna Izquierda (Catálogos) */
          .catalog-list-column {
              height: calc(100vh - 140px); /* Ajustar altura */
              overflow-y: auto;
              min-width: 300px; /* Ancho mínimo para evitar compresión */
              flex-shrink: 0; /* No permitir que se encoja */
          }
          .column-title {
              margin: 0;
              color: #333;
              font-size: 1.25rem;
          }
                  /* Estilos para categorías expandibles */
        .catalog-categories {
          margin-top: 1rem;
        }
        .category-group {
          margin-bottom: 0.5rem;
        }
        .category-header {
          display: flex;
          align-items: center;
          padding: 0.75rem 1rem;
          background-color: #f8f9fa;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s ease;
          border: 1px solid #e9ecef;
        }
        .category-header:hover {
          background-color: #e9ecef;
        }
        .category-header.expanded {
          background-color: #e6effd;
          border-color: #b3d7ff;
        }
        .category-info {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          width: 100%;
        }
        .expand-icon {
          font-size: 1rem;
          transition: transform 0.3s ease;
        }
        .expand-icon.rotated {
          transform: rotate(90deg);
        }
        .category-name {
          font-weight: 600;
          color: #333;
          flex-grow: 1;
        }
        .category-count {
          color: #666;
          font-size: 0.9rem;
        }
        .catalog-list {
          list-style: none;
          padding: 0;
          margin: 0.5rem 0 0 1.5rem;
          border-left: 2px solid #e9ecef;
          padding-left: 1rem;
        }
        .catalog-list-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.5rem 0.75rem;
          margin-bottom: 0.25rem;
          border-radius: 4px;
          cursor: pointer;
          transition: background-color 0.2s ease, color 0.2s ease;
          color: #555;
          border: 1px solid transparent; /* Borde para estado activo */
        }
          .catalog-list-item:hover {
              background-color: #f0f4f8;
          }
          .catalog-list-item.active {
              background-color: #e6effd; /* Azul suave */
              color: #0056b3; /* Azul más oscuro */
              font-weight: 600;
              border-color: #b3d7ff;
          }
          .item-text {
              flex-grow: 1;
              margin-right: 0.5rem;
              overflow: hidden;
              white-space: nowrap;
              text-overflow: ellipsis;
          }
          .item-actions {
              display: flex;
              gap: 0.3rem;
              flex-shrink: 0;
          }
          .btn-icon {
              background: none; border: none; cursor: pointer; padding: 0.2rem;
              font-size: 1rem; line-height: 1; color: #666;
              transition: color 0.2s ease;
          }
          .btn-icon:hover { color: #333; }
          .empty-state {
              color: #888;
              text-align: center;
              padding: 1rem;
              margin-top: 1rem;
          }

          /* Columna Derecha (Detalles) */
          .details-column {
              height: calc(100vh - 140px); /* Ajustar altura */
              overflow-y: auto;
              min-width: 0; /* Permitir que se encoja si es necesario */
              flex-grow: 1; /* Tomar el espacio restante */
          }
          .empty-state-details {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              height: 80%; /* Ocupar la mayor parte del espacio */
              text-align: center;
              color: #777;
          }
          .empty-state-details .icon-lg {
              font-size: 3rem;
              margin-bottom: 1rem;
          }
          .catalog-details { /* Contenedor para cuando SÍ hay catálogo */
              /* Estilos si son necesarios */
          }
          .selected-catalog-title {
              color: #1a2b4d; 
              margin: 0 0 1.5rem;
              padding-bottom: 1rem;
              border-bottom: 1px solid #eee;
          }
          
          /* Estilos Filtros */
          .filter-section {
              background-color: #f8f9fa;
              padding: 1rem 1.5rem;
              border-radius: 6px;
              margin-bottom: 1.5rem;
              border: 1px solid #eee;
          }
          .filter-title {
              margin-top: 0;
              margin-bottom: 1rem;
              font-size: 1.1rem;
              color: #495057;
          }
          .filter-row {
              display: flex;
              flex-wrap: wrap;
              gap: 1rem;
              align-items: flex-end;
          }
          .filter-group {
              flex: 1 1 150px; /* Ajustar base */
              min-width: 150px;
          }
          .select-with-clear {
              position: relative;
              display: flex;
              align-items: center;
          }
          .select-with-clear .form-select {
              padding-right: 2rem; /* Espacio para el botón */
              flex-grow: 1;
          }
          .btn-clear-filter {
              position: absolute;
              right: 0.3rem;
              top: 50%;
              transform: translateY(-50%);
              background: none; border: none; font-size: 1.2rem;
              color: #6c757d; cursor: pointer; padding: 0.2rem;
              line-height: 1;
          }
          .btn-clear-filter:hover { color: #343a40; }
          .filter-apply-btn {
              /* Estilos específicos si se necesitan */
              margin-left: auto; /* Empujar a la derecha si hay espacio */
              align-self: flex-end; /* Asegurar alineación */
          }
          
          /* Estilos Tabla */
          .table-container {
              margin-top: 1.5rem;
          }
          .table-title {
              margin-bottom: 1rem;
              font-size: 1.1rem;
              color: #495057;
          }
          .prices-table {
              /* Opcional: usar table-layout fixed para mejor control de anchos */
              /* table-layout: fixed; */ 
              width: 100%; /* Asegurar que ocupe el ancho disponible */
          }
          .prices-table th, .prices-table td {
             padding: 0.75rem;
             text-align: left;
             border-bottom: 1px solid #dee2e6; /* Usar borde inferior es común */
             vertical-align: middle; /* Alinear verticalmente al centro */
             /* white-space: nowrap; Quitado para permitir wrap */
          }
          /* Estilos específicos para columnas */
          .prices-table th:nth-child(1), /* Columna Dispositivo (Header) */
          .prices-table td:nth-child(1) { /* Columna Dispositivo (Celdas) */
             min-width: 200px; /* Ancho mínimo para el dispositivo */
             white-space: normal; /* Permitir salto de línea */
             word-break: break-word; /* Romper palabras largas si es necesario */
          }
          .prices-table th:nth-child(2), /* Marca */
          .prices-table td:nth-child(2),
          .prices-table th:nth-child(3), /* Almacenamiento */
          .prices-table td:nth-child(3) {
             white-space: nowrap; /* Mantener en una línea */
          }
          .prices-table th:nth-child(n+4), /* Columnas de precios (Header) */
          .prices-table td:nth-child(n+4) { /* Columnas de precios (Celdas) */
             text-align: right; /* Alinear precios a la derecha */
             white-space: nowrap; /* Mantener en una línea */
             min-width: 100px; /* Ancho mínimo para precios */
          }
          
          .empty-state-small {
               color: #888;
               text-align: center;
               padding: 1rem;
               font-size: 0.9rem;
          }

          /* Estilos Mensajes y Resumen */
          .message {
              padding: 0.75rem 1.25rem;
              margin-bottom: 1rem;
              border: 1px solid transparent;
              border-radius: .25rem;
              text-align: center;
          }
          .message-success {
              color: #155724;
              background-color: #d4edda;
              border-color: #c3e6cb;
          }
          .message-error {
              color: #721c24;
              background-color: #f8d7da;
              border-color: #f5c6cb;
          }
          .message-info {
              color: #0c5460;
              background-color: #d1ecf1;
              border-color: #bee5eb;
          }

          /* Estilos Componentes Reutilizados */
          .loading-spinner-small {
              border: 3px solid #f3f3f3; /* Light grey */
              border-top: 3px solid #0066cc; /* Blue */
              border-radius: 50%;
              width: 24px;
              height: 24px;
              animation: spin 1s linear infinite;
              margin: 1rem auto; 
          }
          .form-group { 
              margin-bottom: 1rem; 
          } 
          .form-label { 
              display: block;
              margin-bottom: 0.5rem;
              font-weight: 500;
              color: #4A5568;
          } 
          .form-select, .form-input { 
              width: 100%;
              padding: 0.6rem 0.75rem;
              border: 1px solid #CBD5E0;
              border-radius: 4px;
              font-size: 1rem;
              background-color: #fff;
          }
          .form-select:disabled, .form-input:disabled {
              background-color: #e9ecef;
              cursor: not-allowed;
          }
          /* Añadir estilos base para botones si no existen globalmente */
          .btn {
              padding: 0.6rem 1.2rem;
              border: 1px solid transparent;
              border-radius: 6px;
              cursor: pointer;
              font-weight: 500;
              font-size: 0.95rem;
              transition: background-color 0.2s ease, border-color 0.2s ease;
              text-align: center;
              display: inline-flex; /* Para alinear iconos y texto */
              align-items: center;
              gap: 0.5rem; 
          }
          .btn-primary {
              background-color: #0066cc;
              color: white;
              border-color: #0066cc;
          }
          .btn-primary:hover {
              background-color: #0052a3;
              border-color: #004080;
          }
          .btn-secondary {
              background-color: #6c757d;
              color: white;
              border-color: #6c757d;
          }
          .btn-secondary:hover {
              background-color: #5a6268;
              border-color: #545b62;
          }
          .btn-outline {
              background-color: transparent;
              color: #0066cc;
              border-color: #0066cc;
          }
          .btn-outline:hover {
              background-color: rgba(0, 102, 204, 0.1);
          }
          .btn-sm { /* Añadido para consistencia con Entidades.js */
              padding: 0.3rem 0.8rem;
              font-size: 0.85rem;
          }
          .table-responsive {
              overflow-x: auto;
              margin-top: 1rem;
          }
          .transactions-table {
              width: 100%;
              border-collapse: collapse;
              font-size: 0.9rem;
          }
          .transactions-table th,
          .transactions-table td {
              border: 1px solid #dee2e6;
              padding: 0.75rem;
              text-align: left;
              white-space: nowrap;
          }
          .transactions-table th {
              background-color: #f8f9fa;
              font-weight: 600;
              color: #495057;
          }
          
          /* Media Query (Adaptado de Entidades) */
          @media (max-width: 992px) {
              .precios-layout {
                  grid-template-columns: 1fr; /* Apilar columnas */
                  gap: 1rem; /* Menor separación en móvil */
              }
              .catalog-list-column {
                  height: auto; 
                  max-height: 40vh; 
                  margin-bottom: 1.5rem;
                  min-width: auto; /* Remover ancho mínimo en móvil */
              }
              .details-column {
                  height: auto;
                  min-width: auto; /* Remover restricciones de ancho en móvil */
              }
          }
          
          @media (max-width: 768px) {
              .precios-layout {
                  gap: 0.5rem;
              }
              .catalog-list-column {
                  max-height: 35vh;
              }
          }
          
          /* --- Estilos para el Modal (Copiados/Adaptados de Entidades.js) --- */
          .modal-overlay {
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background-color: rgba(0, 0, 0, 0.6);
              display: flex;
              align-items: center;
              justify-content: center;
              z-index: 1000; /* Asegurar que esté por encima */
              padding: 1rem; /* Espacio por si el modal es muy alto */
          }
          .modal-content {
              background: white;
              padding: 2rem;
              border-radius: 8px;
              box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
              width: 90%;
              max-width: 500px; /* Ancho máximo */
              max-height: 90vh; /* Altura máxima */
              overflow-y: auto; /* Scroll si el contenido es muy largo */
              animation: fadeIn 0.3s ease-out;
          }
                    .modal-content h3 {
            margin-top: 0;
            margin-bottom: 1.5rem;
            color: #333;
          }
          .modal-subtitle {
            color: #666;
            font-size: 0.9rem;
            margin-bottom: 1.5rem;
          }
          
          /* Estilos para modal de selección de categoría */
          .category-selection-content {
            margin-bottom: 1.5rem;
          }
          .create-category-toggle {
            margin-top: 1.5rem;
            text-align: center;
          }
          
          .new-category-section {
            margin-top: 1rem;
            padding-top: 1.5rem;
            border-top: 1px solid #eee;
          }
          .new-category-section h4 {
            margin: 0 0 1rem;
            color: #555;
            font-size: 1rem;
          }
          .input-group {
            display: flex;
            gap: 0.5rem;
            align-items: flex-end;
          }
          .input-group .form-input {
            flex: 1;
          }
          .loading-container {
            text-align: center;
            padding: 2rem;
          }
          .loading-container p {
            margin: 0.5rem 0 0;
            color: #666;
          }
          /* Añadir estilo para error dentro del modal si no existe */
          .modal-content .error-message {
              color: #dc3545;
              font-size: 0.9rem;
              margin-top: 0.5rem;
              margin-bottom: 1rem;
          }
          /* Añadir estilo para acciones del modal si no existe */
          .modal-content .form-actions {
             margin-top: 1.5rem;
             display: flex;
             justify-content: flex-end;
             gap: 0.75rem;
          }

          @keyframes spin { /* Necesario para spinner en modal */
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes fadeIn { /* Necesario para animación del modal */
            from { opacity: 0; transform: scale(0.95); }
            to { opacity: 1; transform: scale(1); }
          }
          
          /* Estilos personalizados para react-select */
          :global(.react-select-container) {
              width: 100%;
              position: relative;
          }
          :global(.react-select__control) {
              min-height: 38px;
              border: 1px solid #CBD5E0;
              border-radius: 4px;
          }
          :global(.react-select__control--is-focused) {
              border-color: #0066cc;
              box-shadow: 0 0 0 1px #0066cc;
          }
          :global(.react-select__value-container) {
              padding: 0.4rem 0.75rem;
          }
          :global(.react-select__menu) {
              z-index: 1050 !important; /* Valor más alto para asegurar que quede por encima de otros elementos */
              position: absolute;
              box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
          }
          :global(.react-select__menu-list) {
              max-height: 250px;
              overflow-y: auto;
          }
          :global(.react-select__option--is-focused) {
              background-color: #e6effd;
          }
          :global(.react-select__option--is-selected) {
              background-color: #0066cc;
          }
          :global(.react-select__indicator-separator) {
              background-color: #CBD5E0;
          }
                    :global(.react-select__placeholder) {
            color: #a0aec0;
          }
          
          /* === ESTILOS PARA MODAL DE EXCEL === */
          .excel-modal {
            max-width: 900px;
            max-height: 90vh;
            overflow-y: auto;
          }
          
          .modal-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin: -30px -30px 1.5rem -30px;
            padding: 20px 30px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-radius: 10px 10px 0 0;
          }
          
          .modal-title {
            color: white;
            margin: 0;
            font-size: 1.5rem;
            font-weight: 600;
          }
          
          .modal-close {
            background: none;
            border: none;
            font-size: 1.5rem;
            cursor: pointer;
            color: white;
            padding: 0.25rem;
            line-height: 1;
            transition: opacity 0.2s ease;
          }
          
          .modal-close:hover {
            opacity: 0.8;
          }
          
          /* Indicador de pasos */
          .step-indicator {
            display: flex;
            justify-content: space-between;
            margin-bottom: 2rem;
            padding: 0 1rem;
          }
          
          .step-item {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 0.5rem;
            flex: 1;
            position: relative;
          }
          
          .step-item:not(:last-child)::after {
            content: '';
            position: absolute;
            top: 1rem;
            left: 60%;
            right: -40%;
            height: 2px;
            background: #e5e7eb;
            z-index: 1;
          }
          
          .step-item.active:not(:last-child)::after {
            background: #3b82f6;
          }
          
          .step-icon {
            width: 2rem;
            height: 2rem;
            border-radius: 50%;
            background: #f3f4f6;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1rem;
            position: relative;
            z-index: 2;
          }
          
          .step-item.active .step-icon {
            background: #3b82f6;
            color: white;
          }
          
          .step-item.current .step-icon {
            background: #1d4ed8;
            animation: pulse 2s infinite;
          }
          
          .step-label {
            font-size: 0.8rem;
            color: #6b7280;
            text-align: center;
          }
          
          .step-item.active .step-label {
            color: #374151;
            font-weight: 500;
          }
          
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
          
          /* Sección de carga */
          .upload-section {
            text-align: center;
          }
          
          .upload-area {
            border: 2px dashed #d1d5db;
            border-radius: 8px;
            padding: 3rem 2rem;
            margin: 1rem 0;
            transition: all 0.3s ease;
            cursor: pointer;
            position: relative;
          }
          
          .upload-area:hover,
          .upload-area.drag-active {
            border-color: #3b82f6;
            background: #f0f9ff;
          }
          
          .upload-input {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            opacity: 0;
            cursor: pointer;
          }
          
          .upload-icon {
            font-size: 3rem;
            margin-bottom: 1rem;
          }
          
          .upload-text {
            font-size: 1.1rem;
            color: #374151;
            margin-bottom: 0.5rem;
          }
          
          .upload-link {
            color: #3b82f6;
            text-decoration: underline;
          }
          
          .upload-hint {
            color: #6b7280;
            font-size: 0.9rem;
          }
          
          /* Selección de hojas */
          .sheet-selection {
            text-align: center;
          }
          
          .sheet-list {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
            margin-top: 1rem;
          }
          
          .sheet-item {
            padding: 1rem;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            background: white;
            cursor: pointer;
            transition: all 0.2s ease;
            text-align: left;
          }
          
          .sheet-item:hover {
            border-color: #3b82f6;
            background: #f0f9ff;
          }
          
          .sheet-item.selected {
            border-color: #3b82f6;
            background: #eff6ff;
            color: #1d4ed8;
          }
          
          /* Mapeo de columnas */
          .mapping-grid {
            display: flex;
            flex-direction: column;
            gap: 1rem;
            margin: 1.5rem 0;
          }
          
          .mapping-row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 1.5rem;
            align-items: start;
            padding: 1.25rem;
            border: 1px solid #e5e7eb;
            border-radius: 6px;
            background: #f9fafb;
          }
          
          .field-header {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            margin-bottom: 0.5rem;
          }
          
          .field-icon {
            font-size: 1.2rem;
          }
          
          .field-label {
            font-weight: 600;
            color: #374151;
          }
          
          .field-required {
            color: #ef4444;
            font-weight: bold;
          }
          
          .field-description {
            color: #6b7280;
            font-size: 0.85rem;
            margin: 0;
          }
          
          .mapping-select {
            width: 100%;
            padding: 0.5rem;
            border: 1px solid #d1d5db;
            border-radius: 4px;
            background: white;
            transition: border-color 0.3s ease, background-color 0.3s ease;
          }
          
          .mapping-select.mapping-error {
            border-color: #ef4444;
            background-color: #fef2f2;
            animation: shake 0.5s ease-in-out;
          }
          
          @keyframes shake {
            0%, 100% { transform: translateX(0); }
            25% { transform: translateX(-5px); }
            75% { transform: translateX(5px); }
          }
          
          .mapping-actions {
            text-align: center;
            margin-top: 2rem;
          }
          
          /* Validación de datos */
          .validation-summary {
            background: #f0f9ff;
            padding: 1rem;
            border-radius: 6px;
            margin-bottom: 1.5rem;
            display: flex;
            gap: 2rem;
          }
          
          .summary-item {
            display: flex;
            flex-direction: column;
            align-items: center;
          }
          
          .summary-label {
            color: #374151;
            font-size: 0.9rem;
          }
          
          .summary-value {
            font-weight: 600;
            color: #1d4ed8;
            font-size: 1.2rem;
          }
          
          .summary-detail {
            font-size: 0.8rem;
            color: #6b7280;
            margin-top: 0.25rem;
          }
          
          .error-count {
            color: #dc2626 !important;
          }
          
          .optimization-count {
            color: #059669 !important;
          }
          
          /* Alertas de validación */
          .validation-errors-alert {
            margin-bottom: 1.5rem;
          }
          
          .alert {
            display: flex;
            align-items: flex-start;
            gap: 0.75rem;
            padding: 1rem;
            border-radius: 6px;
            border: 1px solid;
          }
          
          .alert-error {
            background-color: #fef2f2;
            border-color: #fecaca;
            color: #991b1b;
          }
          
          .alert-icon {
            font-size: 1.2rem;
            flex-shrink: 0;
          }
          
          .alert-content {
            flex: 1;
          }
          
          .alert-content strong {
            font-weight: 600;
          }
          
          /* Datos agrupados */
          .grouped-data-preview {
            max-height: 400px;
            overflow-y: auto;
            border: 1px solid #e5e7eb;
            border-radius: 6px;
            margin-bottom: 1.5rem;
          }
          
          .manufacturer-group {
            border-bottom: 1px solid #e5e7eb;
          }
          
          .group-header {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            padding: 1rem;
            background: #f9fafb;
            cursor: pointer;
            transition: background-color 0.2s ease;
          }
          
          .group-header:hover {
            background: #f3f4f6;
          }
          
          .group-header.expanded {
            background: #e5e7eb;
          }
          
          .group-header.has-errors {
            background: #fef2f2;
            border-left: 4px solid #dc2626;
          }
          
          .group-header.has-errors:hover {
            background: #fee2e2;
          }
          
          .group-header.has-errors.expanded {
            background: #fee2e2;
          }
          
          .group-header.has-warnings {
            background: #fef3c7;
            border-left: 4px solid #f59e0b;
          }
          
          .group-header.has-warnings:hover {
            background: #fde68a;
          }
          
          .group-header.has-warnings.expanded {
            background: #fde68a;
          }
          
          .group-header.no-upload {
            background-color: #f8f9fa;
            border-color: #dee2e6;
            color: #6c757d;
          }
          
          .group-header.no-upload:hover {
            background-color: #e9ecef;
          }
          
          .group-header.no-upload.expanded {
            background-color: #e9ecef;
            border-color: #ced4da;
          }
          
          .expand-icon {
            font-size: 0.8rem;
            transition: transform 0.2s ease;
          }
          
          .expand-icon.rotated {
            transform: rotate(90deg);
          }
          
          .group-title {
            font-weight: 600;
            color: #374151;
            flex: 1;
          }
          
          .group-count {
            font-size: 0.9rem;
            color: #6b7280;
          }
          
          .models-container {
            padding: 0 1rem;
          }
          
          .model-group {
            margin-bottom: 1rem;
          }
          
          .model-header {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.5rem;
            background: #f0f9ff;
            border-radius: 4px;
            margin-bottom: 0.5rem;
            cursor: pointer;
            transition: background-color 0.2s ease;
          }
          
          .model-header:hover {
            background: #dbeafe;
          }
          
          .model-header.expanded {
            background: #bfdbfe;
          }
          
          .model-header.has-errors {
            background: #fef2f2;
            border-left: 4px solid #dc2626;
          }
          
          .model-header.has-errors:hover {
            background: #fee2e2;
          }
          
          .model-header.has-errors.expanded {
            background: #fee2e2;
          }
          
          .model-header.has-warnings {
            background: #fef3c7;
            border-left: 4px solid #f59e0b;
          }
          
          .model-header.has-warnings:hover {
            background: #fde68a;
          }
          
          .model-header.has-warnings.expanded {
            background: #fde68a;
          }
          
          .model-header.no-upload {
            background: #f8f9fa;
            border-color: #dee2e6;
            color: #6c757d;
          }
          
          .model-header.no-upload:hover {
            background: #e9ecef;
          }
          
          .model-header.no-upload.expanded {
            background: #e9ecef;
            border-color: #ced4da;
          }
          
          .model-title {
            font-weight: 500;
            color: #1d4ed8;
            flex: 1;
          }
          
          .model-header.no-upload .model-title {
            color: #6c757d;
          }
          
          .model-count {
            font-size: 0.8rem;
            color: #6b7280;
          }
          
          .error-indicator {
            font-size: 1rem;
            color: #dc2626;
            margin-left: 0.5rem;
            animation: pulse-error 2s infinite;
          }
          
          .warning-indicator {
            font-size: 1rem;
            color: #f59e0b;
            margin-left: 0.5rem;
            animation: pulse-warning 2s infinite;
          }
          
          @keyframes pulse-error {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.6; }
          }
          
          @keyframes pulse-warning {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
          }
          
          .records-table {
            margin-left: 1rem;
          }
          
          .records-table table {
            width: 100%;
            border-collapse: collapse;
            font-size: 0.9rem;
          }
          
          .records-table th,
          .records-table td {
            padding: 0.5rem;
            text-align: left;
            border-bottom: 1px solid #e5e7eb;
          }
          
          .records-table th {
            background: #f9fafb;
            font-weight: 500;
            color: #374151;
          }
          
          .new-record {
            background: #f0fdf4;
          }
          
          .existing-record {
            background: #fef3c7;
          }
          
          .will-be-sent {
            border-left: 3px solid #059669;
          }
          
          .will-be-skipped {
            opacity: 0.6;
            background: #f9fafb !important;
            border-left: 3px solid #9ca3af;
          }
          
          .field-with-status {
            position: relative;
            display: flex;
            align-items: center;
            gap: 0.5rem;
          }
          
          .skip-indicator {
            font-size: 0.9rem;
            opacity: 0.7;
          }
          
          .inline-edit-input {
            width: 100%;
            padding: 0.25rem 0.5rem;
            border: 1px solid #d1d5db;
            border-radius: 3px;
            font-size: 0.9rem;
          }
          
          .inline-edit-input:focus {
            outline: none;
            border-color: #3b82f6;
            box-shadow: 0 0 0 1px #3b82f6;
          }
          
          .inline-edit-input.error {
            border-color: #dc2626;
            background-color: #fef2f2;
          }
          
          .inline-edit-input.error:focus {
            border-color: #dc2626;
            box-shadow: 0 0 0 1px #dc2626;
          }
          
          .inline-edit-input.warning {
            border-color: #f59e0b;
            background-color: #fef3c7;
          }
          
          .inline-edit-input.warning:focus {
            border-color: #f59e0b;
            box-shadow: 0 0 0 1px #f59e0b;
          }
          
          .inline-edit-input.zero-value {
            border-color: #9ca3af;
            background-color: #f9fafb;
            color: #6b7280;
          }
          
          .inline-edit-input.zero-value:focus {
            border-color: #9ca3af;
            box-shadow: 0 0 0 1px #9ca3af;
          }
          
          .inline-edit-input.zero-change-existing {
            color: #9ca3af;
          }
          
          .price-input {
            text-align: right;
          }
          
          .manufacturer-model-cell {
            position: relative;
            padding: 0.5rem;
            min-width: 150px;
          }
          
          .manufacturer-model-display {
            font-size: 0.85rem;
            line-height: 1.3;
            margin-right: 30px;
          }
          
          .model-text {
            color: #666;
            font-size: 0.8rem;
          }
          
          .edit-manufacturer-btn {
            position: absolute;
            top: 50%;
            right: 8px;
            transform: translateY(-50%);
            background: none;
            border: none;
            cursor: pointer;
            padding: 4px;
            border-radius: 4px;
            font-size: 0.9rem;
            opacity: 0.7;
            transition: all 0.2s ease;
          }
          
          .edit-manufacturer-btn:hover {
            opacity: 1;
            background-color: #f0f0f0;
          }
          
          .modal-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 1rem 1.5rem;
            border-bottom: 1px solid #e9ecef;
          }
          
          .modal-header h3 {
            margin: 0;
            color: #333;
          }
          
          .modal-close {
            background: none;
            border: none;
            font-size: 1.5rem;
            cursor: pointer;
            color: #666;
            padding: 0;
            width: 30px;
            height: 30px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            transition: all 0.2s ease;
          }
          
          .modal-close:hover {
            background-color: #f0f0f0;
            color: #333;
          }
          
          .modal-body {
            padding: 1.5rem;
          }
          
          .modal-footer {
            display: flex;
            justify-content: flex-end;
            gap: 0.75rem;
            padding: 1rem 1.5rem;
            border-top: 1px solid #e9ecef;
          }
          
          .status-badge {
            padding: 0.25rem 0.5rem;
            border-radius: 3px;
            font-size: 0.8rem;
            font-weight: 500;
          }
          
          .status-new {
            background: #dcfce7;
            color: #166534;
          }
          
          .status-update {
            background: #fef3c7;
            color: #92400e;
          }
          
          .data-preview {
            margin: 1.5rem 0;
          }
          
          .preview-table {
            border: 1px solid #e5e7eb;
            border-radius: 6px;
            overflow: hidden;
          }
          
          .preview-table table {
            width: 100%;
            border-collapse: collapse;
          }
          
          .preview-table th,
          .preview-table td {
            padding: 0.75rem;
            text-align: left;
            border-bottom: 1px solid #e5e7eb;
          }
          
          .preview-table th {
            background: #f9fafb;
            font-weight: 600;
            color: #374151;
          }
          
          .preview-note {
            padding: 0.75rem;
            background: #f9fafb;
            color: #6b7280;
            font-size: 0.9rem;
            text-align: center;
            margin: 0;
          }
          
          .catalog-name-input {
            margin: 1.5rem 0;
          }
          
          .form-hint {
            color: #6b7280;
            font-size: 0.85rem;
            margin-top: 0.5rem;
            display: block;
          }
          
          .validation-actions {
            display: flex;
            justify-content: space-between;
            gap: 1rem;
            margin-top: 2rem;
          }
          
          /* Estilos para resumen de alertas */
          .alert-summary {
            background: linear-gradient(135deg, #fef3c7 0%, #fed7aa 100%);
            border: 1px solid #f59e0b;
            border-radius: 8px;
            padding: 1rem;
            margin-bottom: 1.5rem;
            box-shadow: 0 2px 4px rgba(245, 158, 11, 0.1);
          }
          
          .alert-summary-content {
            display: flex;
            align-items: center;
            gap: 1rem;
          }
          
          .alert-summary-icon {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            position: relative;
          }
          
          .alert-icon {
            font-size: 1.5rem;
            animation: bounce 2s infinite;
          }
          
          .alert-count {
            background: #dc2626;
            color: white;
            border-radius: 50%;
            width: 24px;
            height: 24px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 0.8rem;
            font-weight: bold;
            position: absolute;
            top: -8px;
            right: -8px;
          }
          
          .alert-summary-text {
            flex: 1;
          }
          
          .alert-title {
            font-weight: 600;
            color: #92400e;
            display: block;
            margin-bottom: 0.25rem;
          }
          
          .alert-breakdown {
            display: flex;
            gap: 1rem;
            font-size: 0.9rem;
          }
          
          .error-count {
            color: #dc2626;
            font-weight: 500;
          }
          
          .warning-count {
            color: #f59e0b;
            font-weight: 500;
          }
          
          .alert-details-btn {
            background: #f59e0b;
            color: white;
            border: none;
            padding: 0.5rem 1rem;
            border-radius: 6px;
            cursor: pointer;
            font-weight: 500;
            transition: background-color 0.2s ease;
          }
          
          .alert-details-btn:hover {
            background: #d97706;
          }
          
          @keyframes bounce {
            0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
            40% { transform: translateY(-10px); }
            60% { transform: translateY(-5px); }
          }
          
          /* Estilos para tabla y paginación */
          .table-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 1rem;
          }
          
          .table-info {
            display: flex;
            gap: 1rem;
            font-size: 0.9rem;
            color: #6b7280;
          }
          
          .records-count {
            font-weight: 500;
          }
          
          .page-info {
            color: #9ca3af;
          }
          
          .pagination-controls {
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 0.5rem;
            margin-top: 1.5rem;
            padding: 1rem;
            border-top: 1px solid #e5e7eb;
          }
          
          .page-numbers {
            display: flex;
            gap: 0.25rem;
          }
          
          .pagination-controls .btn {
            min-width: 40px;
            justify-content: center;
          }
          
          .pagination-controls .btn:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }
          
          /* Responsividad */
          @media (max-width: 768px) {
            .excel-modal {
              max-width: 95vw;
              margin: 1rem;
            }
            
            .step-indicator {
              flex-direction: column;
              gap: 1rem;
            }
            
            .step-item:not(:last-child)::after {
              display: none;
            }
            
            .mapping-row {
              grid-template-columns: 1fr;
              gap: 0.5rem;
            }
            
            .validation-actions {
              flex-direction: column;
            }
            
            .validation-summary {
              flex-direction: column;
              gap: 1rem;
            }
            
            .grouped-data-preview {
              max-height: 300px;
            }
            
            .records-table {
              overflow-x: auto;
              margin-left: 0;
            }
            
            .records-table table {
              min-width: 600px;
            }
            
            .inline-edit-input {
              min-width: 80px;
            }
            
            .alert-summary-content {
              flex-direction: column;
              gap: 0.75rem;
              text-align: center;
            }
            
            .alert-breakdown {
              flex-direction: column;
              gap: 0.5rem;
            }
            
            .table-header {
              flex-direction: column;
              gap: 0.5rem;
              align-items: flex-start;
            }
            
            .table-info {
              flex-direction: column;
              gap: 0.25rem;
            }
            
            .pagination-controls {
              flex-wrap: wrap;
              gap: 0.25rem;
            }
            
            .page-numbers {
              order: 2;
              width: 100%;
              justify-content: center;
            }
          }
          
      `}</style>
    </div>
  );
}
